'use client';
import React, { useRef, useEffect, useState } from "react";
import { createPortal, flushSync } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import Head from "next/head";
import { useRouter } from "next/router";
import dynamic from "next/dynamic";
import { Spinner } from "../../ui/spinner";
import {
  Star,
  TrendingUp, Calendar, Eye,
  SlidersHorizontal, X, Filter, Search, Shuffle, Maximize2, Loader2
} from "lucide-react";

import {
  isPlanningFeedAddedToday,
  isPlanningFeedAddedYesterday,
  getPlanningFeedAddedDate,
} from '../../../utils/addedToday';

// Import Context Provider
import { usePlanningContext } from "../state/PlanningContext";
import { usePlanningSearch } from "../state/PlanningSearchContext";

// Import Layout Components
import Sidebar from "./Sidebar";
import HamburgerButton from "./HamburgerButton";

// Import Common Components
import NotificationToast from "../common/NotificationToast";
import PlanningUpdatesRegionSwitch from "./PlanningUpdatesRegionSwitch";
import discoveryStyles from "../../../styles/home-discovery.module.css";
import CouncilCoverageNotice from "../CouncilCoverageNotice";
import EnhancedStatsBar from "../common/EnhancedStatsBar";
import EnhancedLoadingScreen from "../common/EnhancedLoadingScreen";
import AccessibilityHelper from "../common/AccessibilityHelper";

// Import Filter Components
import ScaleRatingFilter from "../filters/ScaleRatingFilter";
import SearchPanel from "../search/SearchPanel";
import GlobalSearchBar from "../search/GlobalSearchBar";
import LondonSourceFilters from "../search/LondonSourceFilters";
import AppliedFiltersBar from "../search/AppliedFiltersBar";
// import ProjectCategoryFilter from "../filters/ProjectCategoryFilter";
import CategoryDock from "../filters/CategoryDock";
import { AISummariesToggle, SortByDateToggle, GroupedApplicationsToggle } from "../filters/ToggleButtons";
import ScrapedDateFilter from "../filters/ScrapedDateFilter";
import DecisionStatusFilter from "../filters/DecisionStatusFilter";
// import GroupingModeDropdown from "../filters/SmartGroupingToggle"; // Removed - now using backend group_id
import PresetFilters from "../filters/PresetFilters";

import {
  hasActivePlanningSearch,
  selectPlanningDashboardApplications,
  selectPlanningGroupedApplications,
} from "../../../lib/planning/searchWorkbench";
import { PLANNING_ALERTS_ENABLED } from "../../../lib/planningAlertsFeatureFlag";
import {
  buildPlanningSemanticTagUrl,
  filterPlanningTagSearchSlugsForQuery,
  formatPlanningTagLabel,
  shouldReplacePlanningTextQueryWithTags,
} from "../../../lib/planning/tags";
import {
  countBucket,
  textLengthBucket,
  trackPlanningUpdatesEvent,
} from "../../../lib/planningUpdatesAnalytics";
import {
  AGGREGATE_COUNCIL_SLUG,
  COUNCILS,
  LONDON_COUNCILS,
  DEFAULT_COUNCIL_SLUG,
  applicationIdentityKey,
  getCouncilConfig,
  isLegacyCouncil,
} from "../../../lib/councils";
import {
  getPlanningSeoPlaceName,
} from "../../../lib/planningSeo";
import {
  PLANNING_UPDATES_RESULTS_SCROLL_KEY,
  PLANNING_UPDATES_RESULTS_SCROLL_VALUE,
} from "../../../lib/planning/homepageSearchScroll";
import { usePlanningAiAvailability } from "../../../lib/planning/aiAvailability";
import { isLondonScope, readLondonCoverage } from '../../../lib/services/londonPlanningService';
import { usesRetainedLondonDelivery } from '../../../lib/planning/londonSharedDelivery';

// Dynamic imports for tab content
const GridView = dynamic(() => import("../GridView"), {
  loading: () => (
    <div className="flex w-full justify-center py-10">
      <Spinner size={24} className="text-[var(--c-accent)]" />
    </div>
  ),
});

const PlanningTimeline = dynamic(() => import("../../planning-timeline"), {
  loading: () => (
    <div className="flex w-full justify-center py-10">
      <Spinner size={24} className="text-[var(--c-accent)]" />
    </div>
  ),
  ssr: false,
});

const PlanningApplicationModal = dynamic(() => import("../PlanningApplicationModal"), {
  loading: () => null,
  ssr: false,
});

const ReportsView = dynamic(() => import("../views/ReportsView"), {
  loading: () => (
    <div className="flex w-full justify-center py-10">
      <Spinner size={24} className="text-[var(--c-accent)]" />
    </div>
  ),
  ssr: false,
});

const ImageModal = dynamic(() => import("../common/ImageModal"), {
  loading: () => null,
  ssr: false,
});

const NewspaperView = dynamic(() => import("../NewspaperView"), {
  loading: () => (
    <div className="flex w-full justify-center py-10">
      <Spinner size={24} className="text-[var(--c-accent)]" />
    </div>
  ),
  ssr: false,
});

const MapView = dynamic(() => import("../views/MapView"), {
  loading: () => (
    <div className="flex w-full justify-center py-10">
      <Spinner size={24} className="text-[var(--c-accent)]" />
    </div>
  ),
  ssr: false,
});

// Tab content components (to be implemented)
const AnalyticsTab = dynamic(() => import("../tabs/AnalyticsTab"), {
  loading: () => <div className="flex justify-center py-10"><Spinner size={24} /></div>,
  ssr: false,
});

const ProjectsTab = dynamic(() => import("../tabs/ProjectsTab"), {
  loading: () => <div className="flex justify-center py-10"><Spinner size={24} /></div>,
  ssr: false,
});

const FeaturedTab = dynamic(() => import("../tabs/FeaturedTab"), {
  loading: () => <div className="flex justify-center py-10"><Spinner size={24} /></div>,
  ssr: false,
});

const SubscriptionsTab = dynamic(() => import("../tabs/SubscriptionsTab"), {
  loading: () => <div className="flex justify-center py-10"><Spinner size={24} /></div>,
  ssr: false,
});

const AdminTab = dynamic(() => import("../tabs/AdminTab"), {
  loading: () => <div className="flex justify-center py-10"><Spinner size={24} /></div>,
  ssr: false,
});

const FeaturedVirtualizedGridView = dynamic(() => import("../VirtualizedGridView"), {
  loading: () => (
    <div className="flex w-full justify-center py-10">
      <Spinner size={24} className="text-[var(--c-accent)]" />
    </div>
  ),
});

const FeaturedGroupedApplicationsCard = dynamic(() => import("../../ui/GroupedApplicationsCard"), {
  loading: () => <div className="pr-panel min-h-[420px]" />,
});

// Import featured components and utilities
import { getInterestScore } from "../../../src/lib/interestScore";

let imageSamplingCanvas = null;
let imageSamplingContext = null;
const GRID_GROUP_BATCH_SIZE = 6;
const GRID_INITIAL_GROUP_COUNT = 12;
const GRID_BATCH_LOAD_THRESHOLD_PX = 1800;
const SEARCH_EMPTY_STATE_GRACE_MS = 5000;

const normalizeSearchUrlValue = (value = '') => String(value || '')
  .split(',')
  .map((part) => {
    const trimmed = part.trim();
    if (!trimmed) return '';
    try {
      return decodeURIComponent(trimmed);
    } catch (_error) {
      return trimmed;
    }
  })
  .filter(Boolean)
  .join(',');

const clampUnit = (value) => Math.max(0, Math.min(1, value));

const hasInterestDecision = (app) => typeof app?.interesting === 'boolean';
const getBackendInterestScore = (app) => {
  const confidence = Number(app?.interest_confidence);
  const normalizedConfidence = Number.isFinite(confidence) ? clampUnit(confidence) : 0.75;
  const scaleBonus = Number.isFinite(Number(app?.scale_rating)) ? Number(app.scale_rating) : 0;
  return Math.round(normalizedConfidence * 100 + scaleBonus);
};

const parseRgbColor = (value) => {
  if (!value || typeof value !== 'string') return null;
  if (value === 'transparent') return { r: 0, g: 0, b: 0, a: 0 };

  const match = value.match(/rgba?\(([^)]+)\)/i);
  if (!match) return null;

  const parts = match[1].split(',').map(part => part.trim());
  if (parts.length < 3) return null;

  const r = Number(parts[0]);
  const g = Number(parts[1]);
  const b = Number(parts[2]);
  const a = parts.length > 3 ? Number(parts[3]) : 1;

  if (![r, g, b, a].every(Number.isFinite)) return null;
  return { r, g, b, a };
};

const toRelativeLuminance = ({ r, g, b }) => {
  const normalize = (channel) => {
    const value = channel / 255;
    if (value <= 0.03928) return value / 12.92;
    return ((value + 0.055) / 1.055) ** 2.4;
  };

  return (
    0.2126 * normalize(r) +
    0.7152 * normalize(g) +
    0.0722 * normalize(b)
  );
};

const getImageSamplingContext = () => {
  if (typeof document === 'undefined') return null;
  if (!imageSamplingCanvas) {
    imageSamplingCanvas = document.createElement('canvas');
    imageSamplingCanvas.width = 1;
    imageSamplingCanvas.height = 1;
    imageSamplingContext = imageSamplingCanvas.getContext('2d', { willReadFrequently: true });
  }
  return imageSamplingContext;
};

const sampleImageLuminanceAtPoint = (imageElement, x, y) => {
  if (!(imageElement instanceof HTMLImageElement)) return null;
  if (!imageElement.complete || !imageElement.naturalWidth || !imageElement.naturalHeight) return null;

  const rect = imageElement.getBoundingClientRect();
  if (!rect.width || !rect.height) return null;

  const xRatio = clampUnit((x - rect.left) / rect.width);
  const yRatio = clampUnit((y - rect.top) / rect.height);
  const sourceX = Math.round(xRatio * Math.max(0, imageElement.naturalWidth - 1));
  const sourceY = Math.round(yRatio * Math.max(0, imageElement.naturalHeight - 1));

  const context = getImageSamplingContext();
  if (!context) return null;

  try {
    context.clearRect(0, 0, 1, 1);
    context.drawImage(imageElement, sourceX, sourceY, 1, 1, 0, 0, 1, 1);
    const data = context.getImageData(0, 0, 1, 1).data;
    if (!data || data.length < 4) return null;
    if (data[3] <= 10) return null;
    return toRelativeLuminance({ r: data[0], g: data[1], b: data[2] });
  } catch (_error) {
    return null;
  }
};

const getElementBackgroundLuminance = (startElement) => {
  let current = startElement instanceof HTMLElement ? startElement : null;

  while (current) {
    const parsed = parseRgbColor(window.getComputedStyle(current).backgroundColor);
    if (parsed && parsed.a > 0.08) {
      return toRelativeLuminance(parsed);
    }
    current = current.parentElement;
  }

  return null;
};

const getSurfaceLuminanceAtPoint = (x, y, ignoredRoot) => {
  if (typeof document === 'undefined') return null;

  const stack = document.elementsFromPoint(x, y);
  for (const element of stack) {
    if (!(element instanceof HTMLElement)) continue;
    if (ignoredRoot && ignoredRoot.contains(element)) continue;
    if (element.id === '__next') continue;

    if (element instanceof HTMLImageElement) {
      const imageLuminance = sampleImageLuminanceAtPoint(element, x, y);
      if (imageLuminance != null) return imageLuminance;
    }

    const bgLuminance = getElementBackgroundLuminance(element);
    if (bgLuminance != null) return bgLuminance;
  }

  return null;
};

const WALKTHROUGH_GUIDE = {
  overview: {
    title: 'Walkthrough Mode',
    description: 'Click key controls while walkthrough mode is on to get quick explanations.',
    points: [
      'Major: narrows results to high-impact applications (scale 7-10).',
      'Favourites: shows only applications you have starred.',
      'Scale 5+: filters to medium/high impact applications.',
      'Grouped: limits to addresses with related applications.',
      'AI summaries: toggles AI-generated summaries in cards and modal.',
      'Show advanced options: reveals location + radius search controls.'
    ],
    toast: 'Walkthrough mode enabled. Click key controls to see tips.'
  },
  major: {
    title: 'Major Filter',
    description: 'Focuses on larger developments by setting impact scale to 7-10.',
    points: [
      'Use this when you want strategic or city-shaping proposals first.',
      'Toggle again to clear and return to the full scale range.'
    ],
    toast: 'Major: now showing scale 7-10 applications.'
  },
  favorites: {
    title: 'Favourites Filter',
    description: 'Shows only applications you have already favourited.',
    points: [
      'Good for monitoring the shortlist you care about most.',
      'Use this with search or category filters for tighter tracking.'
    ],
    toast: 'Favourites: toggled shortlist-only view.'
  },
  scaleRange: {
    title: 'Scale 5+ Filter',
    description: 'Sets scale range to 5-10 for medium-to-high impact projects.',
    points: [
      'Broader than Major, useful when you need bigger context.',
      'Toggle again to reset the scale range.'
    ],
    toast: 'Scale 5+: showing medium and high impact projects.'
  },
  grouped: {
    title: 'Grouped Filter',
    description: 'Shows grouped application clusters at the same address/site.',
    points: [
      'Useful for phased or repeated submissions at one location.',
      'Helps spot evolving projects over time.'
    ],
    toast: 'Grouped: toggled grouped applications filter.'
  },
  aiSummaries: {
    title: 'AI Summaries Toggle',
    description: 'Controls whether cards and modals prefer AI summaries.',
    points: [
      'Turn on for rapid scanning.',
      'Turn off to read original descriptions only.'
    ],
    toast: 'AI summaries: visibility toggled.'
  },
  searchArea: {
    title: 'Advanced Search Area',
    description: 'Shows or hides location and radius controls for spatial filtering.',
    points: [
      'Set a center point and radius to scope results geographically.',
      'Use alongside category and scale filters for precise triage.'
    ],
    toast: 'Advanced search area toggled.'
  }
};

const TAG_BUCKETS = [
  { id: 'residential', label: 'Residential', tone: 'residential', tags: ['windows_doors', 'house_extension', 'dwellinghouse', 'dormer', 'room_creation', 'rear_extension', 'loft_conversion', 'garage_conversion', 'kitchen_diner', 'living_area_enlargement', 'side_extension', 'front_extension', 'porch', 'bay_window', 'outbuilding', 'short_let', 'hmo', 'pbsa'] },
  { id: 'heritage', label: 'Heritage', tone: 'heritage', tags: ['listed_building', 'lb_consent_context', 'non_material_variation', 'heritage_asset', 'conservation_area', 'place_of_worship'] },
  { id: 'commercial', label: 'Commercial', tone: 'commercial', tags: ['advertisement', 'change_of_use', 'shopfront', 'food_drink', 'retail', 'office', 'hotel', 'mixed_use', 'signage', 'drive_thru'] },
  { id: 'infrastructure', label: 'Infrastructure', tone: 'infrastructure', tags: ['telecomms', 'telecommunications_mast', 'mechanical_plant', 'solar', 'flues_ventilation', 'external_lighting', 'ev_charging', 'heat_pump', 'battery_storage', 'substation', 'data_centre'] },
  { id: 'works', label: 'Works', tone: 'works', tags: ['demolition', 'conversion', 'alterations', 'roof_works', 'roof_terrace', 'landscaping', 'basement', 'access', 'parking'] },
];

const TAG_SIGNAL_COLORS = {
  residential: ['#D35400', '#B9470C', '#92310A', '#C87412', '#7A2A0B', '#A65A0A', '#5F2309', '#E08413'],
  heritage: ['#4C8A17', '#3D7614', '#2B5911', '#667A12', '#264910', '#5F8F1C', '#1E3D0D', '#476B15'],
  commercial: ['#B4144D', '#9B123F', '#741030', '#C02A5F', '#84214E', '#A11B63', '#580A25', '#C94A75'],
  infrastructure: ['#0875AF', '#00659B', '#0D4F7A', '#1389C4', '#114566', '#0D6F9C', '#083552', '#2D95C6'],
  works: ['#74406F', '#633260', '#4D2A52', '#835083', '#442345', '#6E436E', '#302036', '#8D628D'],
  more: ['#8C5A2E', '#A36A35', '#6B4325', '#B8793D'],
};

const getTagSignalColor = (bucket, slug) => {
  const colors = TAG_SIGNAL_COLORS[bucket.id] || TAG_SIGNAL_COLORS.more;
  const index = bucket.tags.indexOf(slug);
  if (index >= 0) return colors[index % colors.length];
  const hash = String(slug || '').split('').reduce((sum, char) => sum + char.charCodeAt(0), 0);
  return colors[hash % colors.length];
};

const getBucketAccentColor = (bucket) => (TAG_SIGNAL_COLORS[bucket.id] || TAG_SIGNAL_COLORS.more)[0];

const TAG_SURPRISE_HISTORY_KEY = 'cityscope:tag-surprise-history';
const normalizeTagHistoryKey = (value) => String(value || '').trim().toLowerCase().replace(/\s+/g, ' ');
const getTagBucket = (slug) => TAG_BUCKETS.find((bucket) => bucket.tags.includes(slug)) || { id: 'more', label: 'More tags', tone: 'zinc', tags: [] };

function ApplicationTagSelector({ compact = false }) {
  const { facetCounts, facetState, resultCount, searchTokens, clearTokens, toggleTag, setTags, clearTags } = usePlanningSearch();
  const [aiInput, setAiInput] = React.useState('');
  const [aiStatus, setAiStatus] = React.useState(null);
  const [aiMode, setAiMode] = React.useState(null);
  const { status: aiAvailability, markAvailable: markAiAvailable, markUnavailable: markAiUnavailable } = usePlanningAiAvailability();
  const [history, setHistory] = React.useState([]);
  const [modalOpen, setModalOpen] = React.useState(false);
  const [modalSearch, setModalSearch] = React.useState('');
  const [modalBucketFilter, setModalBucketFilter] = React.useState('all');
  const modalSearchBucketRef = React.useRef('0');
  const aiInputBucketRef = React.useRef('0');
  const selectorViewedRef = React.useRef(false);
  const tagResultsSignatureRef = React.useRef('');
  const modalEmptySearchSignatureRef = React.useRef('');

  React.useEffect(() => {
    try {
      const parsed = JSON.parse(window.localStorage.getItem(TAG_SURPRISE_HISTORY_KEY) || '[]');
      if (Array.isArray(parsed)) setHistory(parsed.filter((item) => typeof item === 'string').slice(-12));
    } catch (_) {
      // Ignore malformed local tag history.
    }
  }, []);

  const selectedTags = React.useMemo(() => facetState.tags || [], [facetState.tags]);
  const selectedSet = React.useMemo(() => new Set(selectedTags), [selectedTags]);
  const countByTag = React.useMemo(() => new Map((facetCounts.tags || []).map((tag) => [tag.value, tag.count])), [facetCounts.tags]);
  const tagItems = React.useMemo(() => {
    const slugs = Array.from(new Set([
      ...TAG_BUCKETS.flatMap((bucket) => bucket.tags),
      ...(facetCounts.tags || []).map((tag) => tag.value),
      ...selectedTags,
    ]));
    return slugs.map((slug) => {
      const bucket = getTagBucket(slug);
      return {
        slug,
        label: (facetCounts.tags || []).find((tag) => tag.value === slug)?.label || formatPlanningTagLabel(slug),
        count: countByTag.get(slug) || 0,
        bucketId: bucket.id,
        tone: bucket.tone,
        color: getTagSignalColor(bucket, slug),
      };
    }).sort((left, right) => right.count - left.count || left.label.localeCompare(right.label));
  }, [countByTag, facetCounts.tags, selectedTags]);
  const tagItemBySlug = React.useMemo(() => new Map(tagItems.map((tag) => [tag.slug, tag])), [tagItems]);

  const availableTags = React.useMemo(() => tagItems
    .filter((tag) => tag.count > 0)
    .slice(0, 300)
    .map((tag) => ({ slug: tag.slug, label: tag.label, count: tag.count })), [tagItems]);

  const tagBucketSummary = React.useMemo(() => {
    const summary = new Map();
    tagItems.forEach((tag) => {
      const current = summary.get(tag.bucketId) || { tagCount: 0, applicationCount: 0, selectedCount: 0 };
      current.tagCount += 1;
      current.applicationCount += tag.count || 0;
      if (selectedSet.has(tag.slug)) current.selectedCount += 1;
      summary.set(tag.bucketId, current);
    });
    return summary;
  }, [selectedSet, tagItems]);

  const selectedBucketIds = React.useMemo(() => {
    const ids = new Set();
    selectedTags.forEach((slug) => {
      ids.add(getTagBucket(slug).id);
    });
    return Array.from(ids);
  }, [selectedTags]);

  React.useEffect(() => {
    if (selectorViewedRef.current) return;
    if (tagItems.length === 0) return;
    selectorViewedRef.current = true;
    trackPlanningUpdatesEvent('planning_updates_tag_selector_viewed', {
      placement: compact ? 'tag_selector_compact' : 'tag_selector',
      status: countBucket(availableTags.length),
      value: availableTags.length,
      metadata: {
        available_tag_count: availableTags.length,
        available_tag_bucket: countBucket(availableTags.length),
        visible_bucket_count: tagBucketSummary.size,
        result_count_bucket: countBucket(resultCount),
        selected_tag_count: selectedTags.length
      }
    });
  }, [availableTags.length, compact, resultCount, selectedTags.length, tagBucketSummary.size, tagItems.length]);

  React.useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    if (selectedTags.length === 0) {
      tagResultsSignatureRef.current = '';
      return undefined;
    }
    const signature = `${selectedTags.join('|')}|${resultCount}`;
    if (signature === tagResultsSignatureRef.current) return undefined;
    const handle = window.setTimeout(() => {
      tagResultsSignatureRef.current = signature;
      trackPlanningUpdatesEvent('planning_updates_tag_filter_results_viewed', {
        placement: 'results',
        status: countBucket(resultCount),
        value: resultCount,
        metadata: {
          selected_tag_count: selectedTags.length,
          selected_tag_bucket: countBucket(selectedTags.length),
          selected_bucket_count: selectedBucketIds.length,
          result_count_bucket: countBucket(resultCount)
        }
      });
    }, 550);
    return () => window.clearTimeout(handle);
  }, [resultCount, selectedBucketIds.length, selectedTags]);

  const handleAiInputChange = React.useCallback((event) => {
    const nextValue = event.target.value;
    const nextBucket = textLengthBucket(nextValue);
    setAiInput(nextValue);
    if (nextBucket === '0') {
      aiInputBucketRef.current = '0';
      return;
    }
    if (nextBucket !== aiInputBucketRef.current) {
      aiInputBucketRef.current = nextBucket;
      trackPlanningUpdatesEvent('planning_updates_tag_search_input_started', {
        placement: 'tag_selector',
        status: nextBucket,
        metadata: {
          input_length_bucket: nextBucket,
          selected_tag_count: selectedTags.length,
          available_tag_bucket: countBucket(availableTags.length)
        }
      });
    }
  }, [availableTags.length, selectedTags.length]);

  const rememberSurprise = React.useCallback((query) => {
    const normalized = normalizeTagHistoryKey(query);
    if (!normalized) return;
    setHistory((current) => {
      const next = [...current.filter((item) => normalizeTagHistoryKey(item) !== normalized), query].slice(-12);
      try {
        window.localStorage.setItem(TAG_SURPRISE_HISTORY_KEY, JSON.stringify(next));
      } catch (_) {
        // Ignore localStorage write failures.
      }
      return next;
    });
  }, []);

  const runAiTagSearch = React.useCallback(async (query, mode = 'suggest') => {
    const trimmed = query.trim();
    if (!trimmed || aiAvailability !== 'available') return;
    trackPlanningUpdatesEvent('planning_updates_tag_search_submitted', {
      placement: 'tag_selector',
      status: mode,
      entityType: 'tag_search_mode',
      entityId: mode,
      metadata: {
        input_length_bucket: textLengthBucket(trimmed),
        available_tag_bucket: countBucket(availableTags.length),
        selected_tag_count: selectedTags.length
      }
    });
    setAiMode(mode);
    setAiStatus(null);
    try {
      const response = await fetch('/api/planning-tags/suggest', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: trimmed, max_results: 8, available_tags: availableTags }),
      });
      if (!response.ok) throw new Error('tag search unavailable');
      const data = await response.json();
      if (data.source !== 'ai') throw new Error('AI tag search fell back');
      markAiAvailable();
      const slugs = filterPlanningTagSearchSlugsForQuery(
        trimmed,
        (Array.isArray(data.matches) ? data.matches : []).map((match) => match.slug || '')
      );
      if (slugs.length === 0) {
        trackPlanningUpdatesEvent('planning_updates_tag_search_completed', {
          placement: 'tag_selector',
          status: 'no_match',
          entityType: 'tag_search_mode',
          entityId: mode,
          metadata: {
            input_length_bucket: textLengthBucket(trimmed),
            match_count: 0,
            selected_tag_count: selectedTags.length
          }
        });
        trackPlanningUpdatesEvent('planning_updates_tag_search_no_match', {
          placement: 'tag_selector',
          status: mode,
          entityType: 'tag_search_mode',
          entityId: mode,
          metadata: {
            input_length_bucket: textLengthBucket(trimmed),
            available_tag_bucket: countBucket(availableTags.length),
            selected_tag_count: selectedTags.length
          }
        });
        setAiStatus(`No tag match for "${trimmed}" yet.`);
        return;
      }
      const replaceTextQuery = shouldReplacePlanningTextQueryWithTags(trimmed, searchTokens, data.residual_query);
      if (replaceTextQuery && typeof window !== 'undefined') {
        const nextUrl = buildPlanningSemanticTagUrl(window.location.href, slugs);
        if (nextUrl) window.history.replaceState(window.history.state, '', nextUrl);
      }
      setTags(slugs);
      if (replaceTextQuery) {
        clearTokens();
      }
      trackPlanningUpdatesEvent('planning_updates_tag_search_completed', {
        placement: 'tag_selector',
        status: mode === 'surprise' ? 'surprise_applied' : 'tags_applied',
        entityType: 'tag_search_mode',
        entityId: mode,
        value: slugs.length,
        metadata: {
          input_length_bucket: textLengthBucket(trimmed),
          match_count: slugs.length,
          match_count_bucket: countBucket(slugs.length)
        }
      });
      slugs.slice(0, 8).forEach((slug, index) => {
        const bucket = getTagBucket(slug);
        trackPlanningUpdatesEvent('planning_updates_tag_search_match_applied', {
          placement: 'tag_selector',
          status: mode,
          entityType: 'planning_tag',
          entityId: slug,
          value: index + 1,
          metadata: {
            tag_bucket: bucket.id,
            match_rank: index + 1,
            match_count_bucket: countBucket(slugs.length),
            selected_tag_count: slugs.length
          }
        });
      });
      setAiStatus(`${slugs.length} tag${slugs.length === 1 ? '' : 's'} selected for "${trimmed}".`);
    } catch (_) {
      markAiUnavailable();
      trackPlanningUpdatesEvent('planning_updates_tag_search_failed', {
        placement: 'tag_selector',
        status: mode,
        entityType: 'tag_search_mode',
        entityId: mode,
        metadata: {
          input_length_bucket: textLengthBucket(trimmed),
          available_tag_bucket: countBucket(availableTags.length)
        }
      });
      setAiStatus('AI tag search is unavailable, so no tags were changed.');
    } finally {
      setAiMode(null);
    }
  }, [aiAvailability, availableTags, clearTokens, markAiAvailable, markAiUnavailable, searchTokens, selectedTags.length, setTags]);

  const handleSurprise = React.useCallback(async () => {
    trackPlanningUpdatesEvent('planning_updates_tag_surprise_clicked', {
      placement: 'tag_selector',
      status: 'clicked',
      entityType: 'tag_search_mode',
      entityId: 'surprise',
      metadata: {
        available_tag_bucket: countBucket(availableTags.length),
        history_bucket: countBucket(history.length),
        selected_tag_count: selectedTags.length
      }
    });
    setAiMode('surprise');
    setAiStatus(null);
    try {
      const response = await fetch('/api/planning-tags/surprise', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ available_tags: availableTags, avoid_queries: history }),
      });
      if (!response.ok) throw new Error('AI surprise search unavailable');
      const data = await response.json();
      const query = typeof data.query === 'string' ? data.query : '';
      if (data.source !== 'ai' || !query) throw new Error('AI surprise search fell back');
      markAiAvailable();
      trackPlanningUpdatesEvent('planning_updates_tag_surprise_query_generated', {
        placement: 'tag_selector',
        status: 'api',
        entityType: 'tag_search_mode',
        entityId: 'surprise',
        metadata: {
          history_bucket: countBucket(history.length),
          input_length_bucket: textLengthBucket(query)
        }
      });
      rememberSurprise(query);
      setAiInput(query);
      await runAiTagSearch(query, 'surprise');
    } catch (_) {
      markAiUnavailable();
      setAiStatus('AI tag search is temporarily unavailable.');
    } finally {
      setAiMode(null);
    }
  }, [availableTags, history, markAiAvailable, markAiUnavailable, rememberSurprise, runAiTagSearch, selectedTags.length]);

  const handleToggleTag = React.useCallback((tag, forceInclude) => {
    const selected = selectedSet.has(tag.slug);
    const nextState = forceInclude !== undefined ? forceInclude : !selected;
    trackPlanningUpdatesEvent('planning_updates_tag_selected', {
      placement: modalOpen ? 'tag_matrix_modal' : 'tag_selector',
      status: nextState ? 'selected' : 'removed',
      entityType: 'planning_tag',
      entityId: tag.slug,
      value: tag.count,
      metadata: {
        tag_bucket: tag.bucketId,
        tag_count_bucket: countBucket(tag.count),
        selected_tag_count: selectedTags.length,
        next_selected_tag_count: nextState
          ? selectedTags.length + (selected ? 0 : 1)
          : Math.max(0, selectedTags.length - (selected ? 1 : 0)),
        result_count_bucket: countBucket(resultCount)
      }
    });
    toggleTag(tag.slug, forceInclude);
  }, [modalOpen, resultCount, selectedSet, selectedTags.length, toggleTag]);

  const handleClearTags = React.useCallback(() => {
    trackPlanningUpdatesEvent('planning_updates_tags_cleared', {
      placement: 'tag_selector',
      status: countBucket(selectedTags.length),
      metadata: {
        cleared_tag_count: selectedTags.length
      }
    });
    clearTags();
  }, [clearTags, selectedTags.length]);

  const handleOpenTagMatrix = React.useCallback((bucketId = 'all') => {
    trackPlanningUpdatesEvent('planning_updates_tag_matrix_opened', {
      placement: 'tag_selector',
      status: bucketId === 'all' ? (compact ? 'compact' : 'full') : bucketId,
      entityType: 'tag_bucket',
      entityId: bucketId,
      metadata: {
        available_tag_bucket: countBucket(availableTags.length),
        opened_bucket: bucketId,
        selected_tag_count: selectedTags.length
      }
    });
    setModalBucketFilter(bucketId);
    setModalOpen(true);
  }, [availableTags.length, compact, selectedTags.length]);

  const handleCloseTagMatrix = React.useCallback(() => {
    trackPlanningUpdatesEvent('planning_updates_tag_matrix_closed', {
      placement: 'tag_matrix_modal',
      status: countBucket(selectedTags.length),
      metadata: {
        selected_tag_count: selectedTags.length
      }
    });
    setModalOpen(false);
  }, [selectedTags.length]);

  const handleTagMatrixBucketSelect = React.useCallback((option) => {
    trackPlanningUpdatesEvent('planning_updates_tag_matrix_bucket_selected', {
      placement: 'tag_matrix_modal',
      status: option.id,
      entityType: 'tag_bucket',
      entityId: option.id,
      metadata: {
        available_tag_count: option.count,
        available_tag_bucket: countBucket(option.count),
        selected_bucket_tag_count: option.activeCount,
        selected_tag_count: selectedTags.length
      }
    });
    setModalBucketFilter(option.id);
  }, [selectedTags.length]);

  React.useEffect(() => {
    if (!modalOpen || typeof window === 'undefined') return undefined;
    const previousOverflow = document.body.style.overflow;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        handleCloseTagMatrix();
      }
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleCloseTagMatrix, modalOpen]);

  const handleModalSearchChange = React.useCallback((event) => {
    const nextValue = event.target.value;
    const nextBucket = textLengthBucket(nextValue);
    setModalSearch(nextValue);
    if (nextBucket === '0') {
      modalSearchBucketRef.current = '0';
      return;
    }
    if (nextBucket !== modalSearchBucketRef.current) {
      modalSearchBucketRef.current = nextBucket;
      trackPlanningUpdatesEvent('planning_updates_tag_matrix_searched', {
        placement: 'tag_matrix_modal',
        status: nextBucket,
        metadata: {
          input_length_bucket: nextBucket,
          available_tag_bucket: countBucket(availableTags.length)
        }
      });
    }
  }, [availableTags.length]);

  const renderTag = (tag) => {
    const selected = selectedSet.has(tag.slug);
    const disabled = tag.count === 0 && !selected;
    return (
      <button
        key={tag.slug}
        type="button"
        disabled={disabled}
        aria-pressed={selected}
        aria-label={`${selected ? 'Remove' : 'Add'} ${tag.label} tag${tag.count ? `, ${tag.count.toLocaleString()} applications` : ''}`}
        onClick={() => handleToggleTag(tag)}
        className={`pr-chip shrink-0 ${disabled ? 'cursor-not-allowed opacity-35' : ''}`}
        title={`${tag.label}${tag.count ? ` - ${tag.count.toLocaleString()} applications` : ''}`}
      >
        <span
          aria-hidden
          className="h-1.5 w-1.5 shrink-0 rounded-full"
          style={{ background: selected ? 'currentColor' : tag.color }}
        />
        <span>{tag.label}</span>
        {tag.count > 0 && (
          <span className="pr-chip-count">{tag.count.toLocaleString()}</span>
        )}
      </button>
    );
  };

  const visibleLimit = compact ? 4 : 6;
  const getBucketCandidates = React.useCallback((bucketId) => tagItems
    .filter((tag) => tag.bucketId === bucketId)
    .filter((tag) => tag.count > 0 || selectedSet.has(tag.slug)), [selectedSet, tagItems]);
  const getVisibleBucketTags = React.useCallback((bucketId) => {
    const candidates = getBucketCandidates(bucketId);
    const selectedCandidates = candidates.filter((tag) => selectedSet.has(tag.slug)).slice(0, visibleLimit);
    const selectedSlugs = new Set(selectedCandidates.map((tag) => tag.slug));
    const openSlots = Math.max(visibleLimit - selectedCandidates.length, 0);
    const topCandidates = candidates.filter((tag) => !selectedSlugs.has(tag.slug)).slice(0, openSlots);
    const visibleSlugs = new Set([...selectedCandidates, ...topCandidates].map((tag) => tag.slug));
    return candidates.filter((tag) => visibleSlugs.has(tag.slug));
  }, [getBucketCandidates, selectedSet, visibleLimit]);
  const modalQuery = modalSearch.trim().toLowerCase();
  const selectedTagItems = selectedTags
    .map((tag) => tagItemBySlug.get(tag))
    .filter(Boolean);
  const selectedCountByBucket = React.useMemo(() => {
    const counts = new Map();
    selectedTagItems.forEach((tag) => {
      counts.set(tag.bucketId, (counts.get(tag.bucketId) || 0) + 1);
    });
    return counts;
  }, [selectedTagItems]);
  const modalBucketOptions = React.useMemo(() => {
    const bucketCounts = new Map();
    tagItems.forEach((tag) => {
      bucketCounts.set(tag.bucketId, (bucketCounts.get(tag.bucketId) || 0) + 1);
    });
    const optionForBucket = (id, label) => ({
      id,
      label,
      count: bucketCounts.get(id) || 0,
      activeCount: selectedCountByBucket.get(id) || 0,
    });
    const baseOptions = [
      {
        id: 'all',
        label: 'All',
        count: Array.from(bucketCounts.values()).reduce((sum, count) => sum + count, 0),
        activeCount: selectedTagItems.length,
      },
      ...TAG_BUCKETS.map((bucket) => optionForBucket(bucket.id, bucket.label)),
    ];
    if (bucketCounts.get('more')) {
      baseOptions.push(optionForBucket('more', 'More'));
    }
    return baseOptions.filter((option) => option.id === 'all' || option.count > 0);
  }, [selectedCountByBucket, selectedTagItems.length, tagItems]);
  const filteredModalTags = tagItems.filter((tag) => {
    const matchesBucket = modalBucketFilter === 'all' || tag.bucketId === modalBucketFilter;
    const matchesQuery = !modalQuery || tag.label.toLowerCase().includes(modalQuery) || tag.slug.includes(modalQuery);
    return matchesBucket && matchesQuery;
  });
  const filteredModalTagCount = filteredModalTags.length;
  const activeModalBucketLabel = modalBucketOptions.find((option) => option.id === modalBucketFilter)?.label || 'All';

  const handleClearModalSearch = React.useCallback(() => {
    trackPlanningUpdatesEvent('planning_updates_tag_matrix_search_cleared', {
      placement: 'tag_matrix_modal',
      status: countBucket(filteredModalTagCount),
      metadata: {
        input_length_bucket: textLengthBucket(modalSearch),
        filtered_tag_bucket: countBucket(filteredModalTagCount)
      }
    });
    setModalSearch('');
    modalSearchBucketRef.current = '0';
    modalEmptySearchSignatureRef.current = '';
  }, [filteredModalTagCount, modalSearch]);

  React.useEffect(() => {
    if (!modalOpen || !modalQuery || filteredModalTagCount !== 0) return;
    const signature = `${modalQuery}|${modalBucketFilter}`;
    if (signature === modalEmptySearchSignatureRef.current) return;
    modalEmptySearchSignatureRef.current = signature;
    trackPlanningUpdatesEvent('planning_updates_tag_matrix_empty_results', {
      placement: 'tag_matrix_modal',
      status: modalBucketFilter,
      entityType: 'tag_bucket',
      entityId: modalBucketFilter,
      metadata: {
        input_length_bucket: textLengthBucket(modalSearch),
        selected_tag_count: selectedTags.length,
        available_tag_bucket: countBucket(availableTags.length)
      }
    });
  }, [availableTags.length, filteredModalTagCount, modalBucketFilter, modalOpen, modalQuery, modalSearch, selectedTags.length]);

  return (
    <section aria-label="Application tag matrix" className={`pr-panel text-[var(--c-text-primary)] ${compact ? 'p-3 sm:p-4' : 'p-4 sm:p-6'}`}>
      <div className="flex flex-col gap-3 sm:gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <p className="pr-section-label mb-2">Tags</p>
          <p className="max-w-3xl text-sm leading-6 text-[var(--c-text-secondary)]">
            Cut through the feed by planning signal. Pick tags directly{aiAvailability === 'available' ? ', or describe what you are looking for and let AI translate it into tag filters.' : '.'}
          </p>
        </div>
        <div className="flex w-full shrink-0 items-center gap-4 sm:w-auto">
          <span className="pr-data whitespace-nowrap">{resultCount.toLocaleString()} shown</span>
          <button type="button" onClick={() => handleOpenTagMatrix('all')} className="pr-button pr-button--ghost">
            <Maximize2 className="h-3.5 w-3.5" />
            Browse all tags
          </button>
        </div>
      </div>

      {aiAvailability === 'available' && <div className="mt-5">
        <div className="grid grid-cols-2 gap-2 lg:flex lg:flex-row">
          <label className="relative col-span-2 lg:col-span-1 lg:flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--c-text-tertiary)]" />
            <input value={aiInput} onChange={handleAiInputChange} onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); void runAiTagSearch(aiInput); } }} placeholder="Describe what you are looking for" className="h-10 w-full rounded-[3px] border border-[var(--c-border)] bg-[var(--c-surface)] pl-10 pr-3 text-base text-[var(--c-text-primary)] shadow-none outline-none transition placeholder:text-[var(--c-text-tertiary)] focus:border-[var(--c-text-primary)] sm:text-sm" style={{ backgroundColor: 'var(--c-surface)', color: 'var(--c-text-primary)', WebkitTextFillColor: 'var(--c-text-primary)' }} />
          </label>
          <button type="button" disabled={aiMode !== null || !aiInput.trim()} onClick={() => runAiTagSearch(aiInput)} className="pr-button pr-button--ghost h-10 w-full disabled:cursor-not-allowed disabled:opacity-55 lg:w-auto">
            {aiMode === 'suggest' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
            Find tags
          </button>
          <button type="button" disabled={aiMode !== null} onClick={handleSurprise} className="pr-button pr-button--ghost h-10 w-full disabled:cursor-not-allowed disabled:opacity-55 lg:w-auto">
            {aiMode === 'surprise' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Shuffle className="h-4 w-4" />}
            Surprise me
          </button>
        </div>
        {aiStatus && <p className="mt-2 text-xs text-[var(--c-text-secondary)]">{aiStatus}</p>}
      </div>}

      <div className="mt-5">
        {TAG_BUCKETS.map((bucket) => {
          const bucketTags = getVisibleBucketTags(bucket.id);
          if (bucketTags.length === 0) return null;
          const bucketCandidates = getBucketCandidates(bucket.id);
          const hiddenCount = Math.max(bucketCandidates.length - bucketTags.length, 0);
          const bucketTotal = tagItems.filter((tag) => tag.bucketId === bucket.id).reduce((sum, tag) => sum + tag.count, 0);
          return (
            <section key={bucket.id} className="grid gap-2 border-t border-[var(--c-border)] py-3 first:border-t-0 md:grid-cols-[140px,minmax(0,1fr)] md:items-start md:gap-3">
              <div className="flex items-baseline justify-between gap-2 md:block md:pt-1.5">
                <h5 className="font-['DM_Mono'] text-[11px] font-medium uppercase tracking-[0.12em] text-[var(--c-text-secondary)]">{bucket.label}</h5>
                <p className="pr-data md:mt-0.5 md:whitespace-nowrap">
                  {bucketTotal.toLocaleString()} apps
                </p>
              </div>
              <div className="flex min-w-0 flex-wrap items-center gap-1.5">
                {bucketTags.map((tag) => renderTag(tag))}
                {hiddenCount > 0 && (
                  <button type="button" onClick={() => handleOpenTagMatrix(bucket.id)} aria-label={`Show ${hiddenCount} more ${bucket.label} tags`} className="pr-textlink text-xs">
                    +{hiddenCount} more
                  </button>
                )}
              </div>
            </section>
          );
        })}
        {selectedTagItems.length > 0 && (
          <section className="mt-3 grid gap-3 border-t-2 border-[var(--c-text-primary)] pt-3 md:grid-cols-[140px,1fr]">
            <div className="flex items-start justify-between gap-3 md:block">
              <div>
                <h5 className="font-['DM_Mono'] text-[11px] font-medium uppercase tracking-[0.12em] text-[var(--c-text-primary)]">Selected</h5>
                <p className="pr-data mt-0.5">active filters</p>
              </div>
              <button type="button" onClick={handleClearTags} className="pr-textlink text-xs md:mt-2">
                Clear all
              </button>
            </div>
            <div className="flex max-h-44 flex-wrap items-center gap-1.5 overflow-y-auto pr-1 sm:max-h-none sm:overflow-visible">
              {selectedTagItems.map((tag) => renderTag(tag))}
            </div>
          </section>
        )}
      </div>

      {modalOpen && typeof document !== 'undefined' && createPortal((
        <div className="planning-register fixed inset-0 z-[12000] flex items-end justify-center bg-black/50 px-0 py-0 sm:items-center sm:px-4 sm:py-6" onMouseDown={(event) => { if (event.target === event.currentTarget) handleCloseTagMatrix(); }}>
          <div role="dialog" aria-modal="true" aria-labelledby="tag-matrix-modal-title" className="flex max-h-[92dvh] w-full max-w-6xl flex-col overflow-hidden rounded-t-md border border-[var(--c-border)] bg-[var(--c-bg)] text-[var(--c-text-primary)] shadow-[0_30px_90px_rgba(0,0,0,0.3)] sm:max-h-[88vh] sm:rounded-md">
            <div className="flex flex-col gap-4 border-b border-[var(--c-border)] p-4 sm:p-5 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <h3 id="tag-matrix-modal-title" className="pr-heading">{modalBucketFilter === 'all' ? 'All application tags' : `${activeModalBucketLabel} tags`}</h3>
                <p className="pr-data mt-1">
                  {filteredModalTagCount.toLocaleString()} tag{filteredModalTagCount === 1 ? '' : 's'} available
                  {selectedTags.length > 0 ? ` / ${selectedTags.length} selected` : ''}
                </p>
              </div>
              <div className="flex w-full items-center gap-2 lg:w-auto">
                <label className="relative min-w-0 flex-1 sm:min-w-[260px]"><Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--c-text-tertiary)]" /><input autoFocus value={modalSearch} onChange={handleModalSearchChange} placeholder="Search tags" className="h-10 w-full rounded-[3px] border border-[var(--c-border)] bg-[var(--c-surface)] pl-10 pr-10 text-base text-[var(--c-text-primary)] shadow-none outline-none focus:border-[var(--c-text-primary)] sm:text-sm" style={{ backgroundColor: 'var(--c-surface)', color: 'var(--c-text-primary)', WebkitTextFillColor: 'var(--c-text-primary)' }} />
                  {modalSearch && (
                    <button type="button" onClick={handleClearModalSearch} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-[var(--c-text-secondary)] transition hover:text-[var(--c-text-primary)]" aria-label="Clear tag search">
                      <X className="h-3.5 w-3.5" />
                    </button>
                  )}
                </label>
                <button type="button" onClick={handleCloseTagMatrix} className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[3px] border border-[var(--c-border)] bg-[var(--c-surface)] text-[var(--c-text-secondary)] transition hover:text-[var(--c-text-primary)]" aria-label="Close tag matrix"><X className="h-5 w-5" /></button>
              </div>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto p-4 sm:p-5">
              <div className="-mx-4 mb-4 flex gap-2 overflow-x-auto px-4 pb-2 whitespace-nowrap [-webkit-overflow-scrolling:touch] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden sm:mx-0 sm:flex-wrap sm:px-0 sm:pb-0">
                {modalBucketOptions.map((option) => (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => handleTagMatrixBucketSelect(option)}
                    aria-pressed={modalBucketFilter === option.id}
                    className="pr-chip shrink-0"
                  >
                    <span>{option.label}</span>
                    <span className="pr-chip-count">{option.count}</span>
                    {option.activeCount > 0 && (
                      <span className="pr-chip-count">&middot; {option.activeCount} selected</span>
                    )}
                  </button>
                ))}
              </div>
              {selectedTagItems.length > 0 && (
                <section className="sticky top-0 z-10 mb-4 border-y-2 border-[var(--c-text-primary)] bg-[var(--c-bg)] py-3">
                  <div className="mb-3 flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                    <h4 className="font-['DM_Mono'] text-[11px] font-medium uppercase tracking-[0.12em] text-[var(--c-text-primary)]">Selected tags</h4>
                    <span className="pr-data">{selectedTagItems.length} selected / {resultCount.toLocaleString()} apps shown</span>
                  </div>
                  <div className="flex max-h-32 flex-wrap gap-1.5 overflow-y-auto pr-1 sm:max-h-none sm:overflow-visible">{selectedTagItems.map(renderTag)}</div>
                </section>
              )}
              {filteredModalTagCount === 0 ? (
                <div className="pr-panel p-8 text-center">
                  <h4 className="text-base font-semibold text-[var(--c-text-primary)]">No matching tags</h4>
                  <p className="mt-2 text-sm text-[var(--c-text-secondary)]">Try a shorter search, or clear the search to browse every tag.</p>
                  {modalSearch && (
                    <button type="button" onClick={handleClearModalSearch} className="pr-button pr-button--ghost mt-4">
                      Clear search
                    </button>
                  )}
                </div>
              ) : (
                <div className="grid gap-3 sm:gap-4 lg:grid-cols-2">
                  {TAG_BUCKETS.map((bucket) => {
                    const tags = filteredModalTags.filter((tag) => tag.bucketId === bucket.id);
                    if (tags.length === 0) return null;
                    const selectedInBucket = selectedCountByBucket.get(bucket.id) || 0;
                    return (
                      <section key={bucket.id} className="pr-panel p-3 sm:p-4">
                        <div className="mb-3 flex items-center justify-between gap-3">
                          <h4 className="flex items-center gap-2 font-['DM_Mono'] text-[11px] font-medium uppercase tracking-[0.12em] text-[var(--c-text-secondary)]">
                            <span aria-hidden className="h-1.5 w-1.5 rounded-full" style={{ background: getBucketAccentColor(bucket) }} />
                            {bucket.label}
                          </h4>
                          <div className="flex items-center gap-2">
                            <span className="pr-data">{tags.length} tags</span>
                            {selectedInBucket > 0 && <span className="pr-data text-[var(--c-accent)]">{selectedInBucket} selected</span>}
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-1.5">{tags.map(renderTag)}</div>
                      </section>
                    );
                  })}
                  {filteredModalTags.some((tag) => tag.bucketId === 'more') && (
                    <section className="pr-panel p-3 sm:p-4 lg:col-span-2">
                      <div className="mb-3 flex items-center justify-between gap-3">
                        <h4 className="font-['DM_Mono'] text-[11px] font-medium uppercase tracking-[0.12em] text-[var(--c-text-secondary)]">More tags</h4>
                        {(selectedCountByBucket.get('more') || 0) > 0 && <span className="pr-data text-[var(--c-accent)]">{selectedCountByBucket.get('more')} selected</span>}
                      </div>
                      <div className="flex flex-wrap gap-1.5">{filteredModalTags.filter((tag) => tag.bucketId === 'more').map(renderTag)}</div>
                    </section>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      ), document.body)}
    </section>
  );
}

const DEMO_MODE_ENABLED = process.env.NEXT_PUBLIC_DEMO_MODE === '1';
const PLANNING_UPDATES_LOADER_EVENT = 'planning-updates-loader';
const PLANNING_UPDATES_LOADER_STATE_KEY = '__cityScopePlanningUpdatesLoaderState';
const PLANNING_REGION_ROUTE_EVENT = 'cityscope:planning-region-route';
const REGION_COUNT_FALLBACKS = {
  edinburgh: 6053,
  midlothian: 873,
  'east-lothian': 1223,
  'west-lothian': 831,
  falkirk: 620,
  clackmannanshire: 242,
};
const PUBLIC_REGION_COUNCILS = Object.values(COUNCILS).filter((council) => council.publicEnabled);
const REGION_WHEEL_OPTIONS = [
  { key: 'scotland', label: 'All regions', href: '/planning-updates' },
  { key: 'london', label: 'London', href: '/planning-updates/london' },
  ...PUBLIC_REGION_COUNCILS.map((council) => ({
    key: council.slug,
    label: council.slug === 'edinburgh' ? 'Edinburgh' : council.displayName,
    href: `/planning-updates/${council.shortCode}`,
    countKey: council.slug,
  })),
  ...Object.values(LONDON_COUNCILS).map((council) => ({
    key: council.slug, label: council.displayName,
    href: `/planning-updates/${council.shortCode}`, countKey: council.slug,
  })),
];
const FEED_DISPLAY_COUNT_TOLERANCE = 30;

const getPlanningUpdatesRegionKey = (asPath = '', fallbackCouncilSlug = 'edinburgh') => {
  const cleanPath = String(asPath || '').split('?')[0].split('#')[0];
  if (cleanPath === '/planning-updates') return 'scotland';
  const routeCouncil = cleanPath.split('/').filter(Boolean)[1];
  if (routeCouncil === 'london' || fallbackCouncilSlug === 'london' && !routeCouncil) return 'london';
  const routeCouncilConfig = getCouncilConfig(routeCouncil);
  if (routeCouncilConfig?.slug) return routeCouncilConfig.slug;
  const fallbackCouncil = getCouncilConfig(fallbackCouncilSlug);
  return fallbackCouncil?.slug || DEFAULT_COUNCIL_SLUG;
};

const getPlanningUpdatesRegionDisplayName = (regionKey = 'edinburgh') => {
  if (regionKey === 'london') return 'London';
  if (regionKey === 'scotland') return 'Scotland';
  const council = getCouncilConfig(regionKey);
  if (council?.slug) return getPlanningSeoPlaceName(council.slug);
  return REGION_WHEEL_OPTIONS.find((option) => option.key === regionKey)?.label || 'City of Edinburgh';
};

const getPlanningUpdatesApplicationCouncilSlug = (application) => {
  const rawCouncil =
    application?.source_council ||
    application?.council_slug ||
    application?.council ||
    application?.sourceCouncil;

  if (rawCouncil) {
    const council = getCouncilConfig(rawCouncil);
    if (council?.slug) return council.slug;
  }

  return null;
};

const isPublicPlanningUpdatesApplication = (application) => {
  const applicationCouncilSlug = getPlanningUpdatesApplicationCouncilSlug(application);
  if (!applicationCouncilSlug) return true;
  return getCouncilConfig(applicationCouncilSlug)?.publicEnabled === true;
};

const getPlanningUpdatesApplicationKey = (application, index, prefix) => {
  const identityKey = applicationIdentityKey(application || {});
  if (identityKey) return identityKey;

  const reference = (
    application?.reference_no ||
    application?.reference ||
    application?.ref ||
    application?.application_ref ||
    application?.id ||
    ''
  ).toString().replace(/\s+/g, '').trim().toUpperCase();

  return reference || `${prefix}:${index}`;
};

const mergePlanningUpdatesApplicationSources = (primary = [], fallback = []) => {
  const merged = [];
  const seen = new Set();

  const pushRecord = (record, index, prefix) => {
    if (!record) return;
    const key = getPlanningUpdatesApplicationKey(record, index, prefix);
    if (seen.has(key)) return;
    seen.add(key);
    merged.push(record);
  };

  if (Array.isArray(primary)) {
    primary.forEach((record, index) => pushRecord(record, index, 'primary'));
  }

  if (Array.isArray(fallback)) {
    fallback.forEach((record, index) => pushRecord(record, index, 'fallback'));
  }

  return merged;
};

const filterPlanningUpdatesApplicationsForRegion = (regionKey, source = []) => {
  if (!Array.isArray(source) || source.length === 0) return [];
  if (regionKey === 'london') return source.filter((app) => isLondonScope(app.source_council));
  if (regionKey === AGGREGATE_COUNCIL_SLUG || regionKey === 'scotland') {
    return source.filter((app) => !isLondonScope(app.source_council) && isPublicPlanningUpdatesApplication(app));
  }

  const councilSlug = getCouncilConfig(regionKey)?.slug || regionKey;
  return source.filter((application) => {
    const applicationCouncilSlug = getPlanningUpdatesApplicationCouncilSlug(application);
    if (applicationCouncilSlug) return applicationCouncilSlug === councilSlug;
    return councilSlug === DEFAULT_COUNCIL_SLUG;
  });
};

const scopePlanningUpdatesApplicationsForRegion = (regionKey, primary = [], fallback = []) => (
  mergePlanningUpdatesApplicationSources(
    filterPlanningUpdatesApplicationsForRegion(regionKey, primary),
    filterPlanningUpdatesApplicationsForRegion(regionKey, fallback)
  )
);

const scopePlanningUpdatesGroupsForRegion = (regionKey, groups = []) => {
  if (!Array.isArray(groups) || groups.length === 0) return [];

  return groups
    .map((group) => {
      const scopedApps = filterPlanningUpdatesApplicationsForRegion(regionKey, group?.apps || []);
      return scopedApps.length > 0
        ? { ...group, apps: scopedApps }
        : null;
    })
    .filter(Boolean);
};

const PlanningUpdatesPage = () => {
  const [_isClient, setIsClient] = React.useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');
  
  // Council state
  const [selectedCouncil, setSelectedCouncil] = useState('City of Edinburgh');
  const [regionCounts, setRegionCounts] = useState(REGION_COUNT_FALLBACKS);
  
  React.useEffect(() => {
    setIsClient(true);
  }, []);

  React.useEffect(() => {
    const controller = new AbortController();

    fetch('/api/planning-feed/counts', {
      signal: controller.signal,
      headers: { Accept: 'application/json' },
    })
      .then((response) => (response.ok ? response.json() : null))
      .then((payload) => {
        if (!payload?.counts) return;
        setRegionCounts((current) => ({
          ...current,
          ...Object.fromEntries(
            Object.entries(payload.counts).filter(([, value]) => Number.isFinite(Number(value)))
          ),
        }));
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn('Failed to load planning region wheel counts', error);
        }
      });

    return () => controller.abort();
  }, []);
  
  const router = useRouter();

  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    const url = new URL(window.location.href);
    if (!url.pathname.startsWith('/planning-updates')) return;

    const sourceKey = url.searchParams.has('q')
      ? 'q'
      : url.searchParams.has('tokens')
        ? 'tokens'
        : url.searchParams.has('search')
          ? 'search'
          : null;

    if (!sourceKey) return;

    const rawValue = url.searchParams.get(sourceKey) || '';
    const normalizedValue = normalizeSearchUrlValue(rawValue);
    const hasLegacySearchKey = url.searchParams.has('search') || url.searchParams.has('tokens');

    if (!normalizedValue || (sourceKey === 'q' && rawValue === normalizedValue && !hasLegacySearchKey)) {
      return;
    }

    url.searchParams.set('q', normalizedValue);
    url.searchParams.delete('search');
    url.searchParams.delete('tokens');
    window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
  }, [router.asPath]);
  
  const {
    themeMode,
    viewMode,
    newspaperView,
    mapView,
    isLoading,
    councilConfig,
    switchCouncil,
    applications: contextApplications,
    allApplications,
    referenceDate,
    feedSummary,
    usesRemoteSummary,
    groupedApplications: contextGroupedApplications,
    selectedImage,
    expandedIndex,
    visibleGroupCount,
    cardHoverVariants,
    searchQuery,
    setSearchQuery,
    selectedFilter,
    setSelectedFilter,
    sortByDate,
    handleSetSelectedImage,
    handleCardExpand,
    toggleFavorite,
    favorites,
    setVisibleGroupCount,
    handleViewModeChange,
    selectedCategoryFilter,
    setSelectedCategoryFilter,
    selectedScaleCategory,
    setSelectedScaleCategory,
    scaleRange,
    setScaleRange,
    filterByAddedToday,
    toggleFilterByAddedToday,
    filterByAddedYesterday,
    toggleFilterByAddedYesterday,
    filterByScrapedDate,
    setFilterByScrapedDate,
    decisionStatusFilter,
    setDecisionStatusFilter,
    showToast,
    multipleCategoryFilters,
    setMultipleCategoryFilters,
    setCategoryFilterMode,
    showOnlyGroupedApplications,
    setShowOnlyGroupedApplications,
    showAiSummaries,
    isSidebarOpen,
    backgroundProcessing,
    processingProgress,
    isLoadingFullDataset,
    selectedApplication,
    isApplicationModalOpen,
    closeApplicationModal,
    initialModalTab,
    initialModalDocumentUrl,
    initialModalImageExpanded
  } = usePlanningContext();

  const hasScaleRangeFilter = Number(scaleRange?.min) > 1 || Number(scaleRange?.max) < 10;
  const routeRegionKey = React.useMemo(
    () => getPlanningUpdatesRegionKey(router.asPath, councilConfig?.slug),
    [councilConfig?.slug, router.asPath]
  );
  const [optimisticRegionKey, setOptimisticRegionKey] = React.useState(routeRegionKey);
  const activeRegionKey = optimisticRegionKey || routeRegionKey;
  const londonScope = isLondonScope(activeRegionKey);
  const retainedLondonScope = usesRetainedLondonDelivery(activeRegionKey);
  React.useEffect(() => {
    if (!londonScope) return;
    const controller = new AbortController();
    readLondonCoverage({ signal: controller.signal }).then((coverage) => {
      if (controller.signal.aborted) return;
      setRegionCounts((current) => ({ ...current,
        ...Object.fromEntries(coverage.councils.map((council) => [council.slug, council.applications])),
        london: coverage.councils.reduce((n, council) => n + council.applications, 0),
      }));
    }).catch(() => {});
    return () => controller.abort();
  }, [londonScope]);
  const planningPlaceName = getPlanningUpdatesRegionDisplayName(activeRegionKey)
    || councilConfig?.displayName
    || selectedCouncil
    || 'City of Edinburgh';

  React.useEffect(() => {
    setOptimisticRegionKey(routeRegionKey);
  }, [routeRegionKey]);

  React.useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const syncRegionFromLocation = (event) => {
      const eventRegion = event?.detail?.region;
      if (eventRegion && REGION_WHEEL_OPTIONS.some((option) => option.key === eventRegion)) {
        setOptimisticRegionKey(eventRegion);
        return;
      }

      setOptimisticRegionKey(
        getPlanningUpdatesRegionKey(window.location.pathname, councilConfig?.slug)
      );
    };

    window.addEventListener(PLANNING_REGION_ROUTE_EVENT, syncRegionFromLocation);
    window.addEventListener('popstate', syncRegionFromLocation);

    return () => {
      window.removeEventListener(PLANNING_REGION_ROUTE_EVENT, syncRegionFromLocation);
      window.removeEventListener('popstate', syncRegionFromLocation);
    };
  }, [councilConfig?.slug]);

  React.useEffect(() => {
    if (typeof document === 'undefined') return;
    const nextTitle = activeRegionKey === 'scotland'
      ? "Scotland's Planning Applications Search | City Scope"
      : `View ${planningPlaceName} Planning Applications | City Scope`;
    document.title = nextTitle;

    const rafId = window.requestAnimationFrame(() => {
      document.title = nextTitle;
    });
    const timeoutId = window.setTimeout(() => {
      document.title = nextTitle;
    }, 250);

    return () => {
      window.cancelAnimationFrame(rafId);
      window.clearTimeout(timeoutId);
    };
  }, [activeRegionKey, planningPlaceName, router.asPath]);

  const {
    results,
    clearAll: clearSearchFilters,
    activeChips,
    settledSearchInput,
    isRemoteSearchLoading,
    isRemoteSearchLoadingMore,
    remoteSearchError,
    hasRemoteSearchResultSet,
    hasMoreRemoteSearchResults,
    loadMoreRemoteSearchResults,
    radiusSearchSummary,
    resultCount,
  } = usePlanningSearch();
  const searchModeActive = React.useMemo(
    () => hasActivePlanningSearch({ activeChips, searchInput: settledSearchInput }),
    [activeChips, settledSearchInput]
  );
  const interactiveSearchSignature = React.useMemo(() => JSON.stringify({
    search: settledSearchInput,
    filters: (activeChips || []).map((chip) => `${chip.category}:${chip.id || chip.label}`),
    legacyCategories: multipleCategoryFilters,
    legacyCategory: selectedCategoryFilter,
    legacyScale: selectedScaleCategory,
    legacyScaleRange: scaleRange,
    legacyDecisionStatus: decisionStatusFilter,
    legacyAddedToday: Boolean(filterByAddedToday),
    legacyAddedYesterday: Boolean(filterByAddedYesterday),
    legacyAddedDate: filterByScrapedDate || null,
  }), [
    activeChips,
    decisionStatusFilter,
    filterByAddedToday,
    filterByAddedYesterday,
    filterByScrapedDate,
    multipleCategoryFilters,
    scaleRange,
    selectedCategoryFilter,
    selectedScaleCategory,
    settledSearchInput,
  ]);

  React.useEffect(() => {
    setVisibleGroupCount(GRID_INITIAL_GROUP_COUNT);
  }, [interactiveSearchSignature, setVisibleGroupCount]);

  React.useEffect(() => {
    if (councilConfig?.displayName) {
      setSelectedCouncil(councilConfig.displayName);
    }
  }, [councilConfig?.displayName]);

  const scopedContextApplications = React.useMemo(() => (
    scopePlanningUpdatesApplicationsForRegion(activeRegionKey, contextApplications, allApplications)
  ), [activeRegionKey, allApplications, contextApplications]);

  const scopedSearchResults = React.useMemo(() => (
    filterPlanningUpdatesApplicationsForRegion(activeRegionKey, results)
  ), [activeRegionKey, results]);

  const scopedAllApplications = React.useMemo(() => (
    scopePlanningUpdatesApplicationsForRegion(activeRegionKey, allApplications, contextApplications)
  ), [activeRegionKey, allApplications, contextApplications]);

  const requiresLegacyDashboardDataset = (
    ['favorites', 'reports'].includes(selectedFilter) || showOnlyGroupedApplications
  );
  const hasRemoteDashboardResultSet = hasRemoteSearchResultSet && !requiresLegacyDashboardDataset;
  const hasMoreRemoteDashboardResults = hasRemoteDashboardResultSet && hasMoreRemoteSearchResults;
  const applications = React.useMemo(() => (
    selectPlanningDashboardApplications({
      searchActive: searchModeActive || hasRemoteDashboardResultSet,
      searchResults: scopedSearchResults,
      fallbackApplications: scopedContextApplications,
      allowSearchResultsOutsideFallback: hasRemoteDashboardResultSet,
    })
  ), [hasRemoteDashboardResultSet, searchModeActive, scopedSearchResults, scopedContextApplications]);
  const radiusFallbackActive = Boolean(
    searchModeActive &&
    radiusSearchSummary?.exactHasNoResultsButRadiusHasResults
  );
  const exactLocationEmptyState = Boolean(
    searchModeActive &&
    radiusSearchSummary?.hasLocation &&
    radiusSearchSummary.exactResultCount === 0
  );
  const radiusFallbackVisibleCount = applications.length;

  const addedTodayCount = React.useMemo(() => {
    if (retainedLondonScope) return null;
    if (usesRemoteSummary) return feedSummary?.counts.today ?? null;
    if (!applications || !Array.isArray(applications)) return 0;
    return applications.filter(app => isPlanningFeedAddedToday(app, referenceDate)).length;
  }, [applications, referenceDate, feedSummary, usesRemoteSummary, retainedLondonScope]);

  const addedYesterdayCount = React.useMemo(() => {
    if (retainedLondonScope) return null;
    if (usesRemoteSummary) return feedSummary?.counts.yesterday ?? null;
    if (!applications || !Array.isArray(applications)) return 0;
    return applications.filter(app => isPlanningFeedAddedYesterday(app, referenceDate)).length;
  }, [applications, referenceDate, feedSummary, usesRemoteSummary, retainedLondonScope]);

  const scopedContextGroupedApplications = React.useMemo(() => (
    scopePlanningUpdatesGroupsForRegion(activeRegionKey, contextGroupedApplications)
  ), [activeRegionKey, contextGroupedApplications]);

  const groupedApplications = React.useMemo(() => (
    selectPlanningGroupedApplications({
      searchActive: searchModeActive || hasRemoteDashboardResultSet,
      searchResults: applications,
      fallbackGroups: scopedContextGroupedApplications,
    })
  ), [hasRemoteDashboardResultSet, searchModeActive, applications, scopedContextGroupedApplications]);
  const groupedApplicationCount = Array.isArray(groupedApplications)
    ? groupedApplications.length
    : 0;
  // The remote feed is already row-virtualized by GridView. Re-slicing it into
  // six-group batches causes a full dashboard update on every scroll without
  // reducing the number of mounted cards. Keep progressive reveal for legacy
  // local datasets, but let the virtualizer own remote-feed rendering.
  const remoteGridOwnsRendering = hasRemoteDashboardResultSet;
  const hasMoreGroups = remoteGridOwnsRendering
    ? hasMoreRemoteDashboardResults
    : visibleGroupCount < groupedApplicationCount;

  const lastUpdatedInfo = React.useMemo(() => {
    if (retainedLondonScope) return { label: 'Source coverage under review', isStale: false };
    const sourceApps = Array.isArray(scopedAllApplications) && scopedAllApplications.length > 0
      ? scopedAllApplications
      : scopedContextApplications;

    let latestScrapedAt = usesRemoteSummary && feedSummary?.fetched_at ? new Date(feedSummary.fetched_at) : null;
    if (latestScrapedAt && Number.isNaN(latestScrapedAt.getTime())) latestScrapedAt = null;

    sourceApps.forEach(app => {
      if (usesRemoteSummary) return;
      if (!app?.scraped_date) return;
      const parsed = new Date(app.scraped_date);
      if (Number.isNaN(parsed.getTime())) return;
      if (!latestScrapedAt || parsed > latestScrapedAt) {
        latestScrapedAt = parsed;
      }
    });

    if (!latestScrapedAt) {
      return {
        label: 'Last updated unavailable',
        isStale: true
      };
    }

    const formattedUtc = latestScrapedAt.toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone: 'UTC'
    });

    const TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000;
    const isStale = Date.now() - latestScrapedAt.getTime() > TWENTY_FOUR_HOURS_MS;

    return {
      label: `Last updated ${formattedUtc} UTC`,
      isStale
    };
  }, [scopedAllApplications, scopedContextApplications, feedSummary, usesRemoteSummary, retainedLondonScope]);

  const expectedLoadedTotal = React.useMemo(() => {
    const counts = regionCounts || {};
    if (activeRegionKey === 'scotland') {
      return PUBLIC_REGION_COUNCILS.reduce((total, council) => {
        const count = Number(counts[council.slug]);
        return Number.isFinite(count) ? total + count : total;
      }, 0);
    }
    const count = Number(counts[activeRegionKey]);
    return Number.isFinite(count) ? count : 0;
  }, [activeRegionKey, regionCounts]);

  const totalLoadedCount = React.useMemo(() => {
    const sourceCount = Array.isArray(scopedAllApplications) ? scopedAllApplications.length : 0;
    const visibleCount = Array.isArray(applications) ? applications.length : 0;
    const progressCount = Number(processingProgress) || 0;
    const scopedProgressCount = expectedLoadedTotal > 0
      ? Math.min(progressCount, expectedLoadedTotal)
      : progressCount;

    return Math.max(sourceCount, visibleCount, scopedProgressCount);
  }, [applications, expectedLoadedTotal, processingProgress, scopedAllApplications]);
  const hasUsableFeedRecords = totalLoadedCount > 0 || applications.length > 0;
  const remainingExpectedRecords = Math.max(0, expectedLoadedTotal - totalLoadedCount);
  const feedIsVisiblyPartial = Boolean(
    hasUsableFeedRecords &&
    expectedLoadedTotal > 0 &&
    remainingExpectedRecords > FEED_DISPLAY_COUNT_TOLERANCE
  );
  const isCompletingUsableFeed = Boolean(
    feedIsVisiblyPartial &&
    isLoadingFullDataset
  );
  const isBackgroundHydrating = Boolean(
    (!hasUsableFeedRecords && (isLoading || backgroundProcessing || isLoadingFullDataset)) ||
    isCompletingUsableFeed
  );
  const showHeaderFeedSyncNotice = isBackgroundHydrating;
  const backgroundHydrationPercent = React.useMemo(() => {
    if (!isBackgroundHydrating) return 100;
    if (!expectedLoadedTotal || expectedLoadedTotal <= 0) return null;
    return Math.max(4, Math.min(98, Math.round((totalLoadedCount / expectedLoadedTotal) * 100)));
  }, [expectedLoadedTotal, isBackgroundHydrating, totalLoadedCount]);
  const knownPlanningFeedTotalRef = useRef(
    Math.max(
      Array.isArray(scopedAllApplications) ? scopedAllApplications.length : 0,
      totalLoadedCount,
      500
    )
  );
  const knownPlanningFeedRegionRef = useRef(activeRegionKey);
  const [hasCompletedInitialHydration, setHasCompletedInitialHydration] = useState(
    () => hasUsableFeedRecords || !isBackgroundHydrating
  );
  const initialReadyDelayRef = useRef(null);
  const hydrationOverlayTotalCount = Math.max(
    Number(knownPlanningFeedTotalRef.current) || 0,
    totalLoadedCount
  );
  const hydrationOverlayTitle = isBackgroundHydrating
    ? 'Loading Planning Updates'
    : 'Finalising Planning Updates';
  const hydrationOverlayDescription = isBackgroundHydrating
    ? 'Pulling the latest planning feed and building the dashboard.'
    : 'Finishing the dashboard handoff.';

  const showInitialHydrationOverlay = !hasCompletedInitialHydration && !hasUsableFeedRecords;
  const hydrationOverlayStage = showInitialHydrationOverlay
    ? (isLoading
      ? 'connecting'
      : 'bootstrapping')
    : 'ready';

  React.useEffect(() => {
    const currentScopedTotal = Math.max(
      Array.isArray(scopedAllApplications) ? scopedAllApplications.length : 0,
      totalLoadedCount,
      500
    );

    if (knownPlanningFeedRegionRef.current !== activeRegionKey) {
      knownPlanningFeedRegionRef.current = activeRegionKey;
      knownPlanningFeedTotalRef.current = currentScopedTotal;
      return;
    }

    const knownTotal = Math.max(
      Number(knownPlanningFeedTotalRef.current) || 0,
      currentScopedTotal
    );

    knownPlanningFeedTotalRef.current = knownTotal;
  }, [activeRegionKey, scopedAllApplications, totalLoadedCount]);

  React.useEffect(() => {
    if (hasCompletedInitialHydration) return undefined;
    if (totalLoadedCount <= 0) return undefined;

    if (initialReadyDelayRef.current) {
      window.clearTimeout(initialReadyDelayRef.current);
    }

    initialReadyDelayRef.current = window.setTimeout(() => {
      setHasCompletedInitialHydration(true);
      initialReadyDelayRef.current = null;
    }, 320);

    return () => {
      if (initialReadyDelayRef.current) {
        window.clearTimeout(initialReadyDelayRef.current);
        initialReadyDelayRef.current = null;
      }
    };
  }, [hasCompletedInitialHydration, totalLoadedCount]);

  const hydrationOverlayProgress = React.useMemo(() => {
    if (!showInitialHydrationOverlay) return 100;
    if (isLoading && totalLoadedCount <= 0) return 12;

    const loadedCount = Math.max(
      totalLoadedCount,
      Array.isArray(applications) ? applications.length : 0
    );
    const totalCount = Math.max(Number(knownPlanningFeedTotalRef.current) || 0, 500);
    const hasReliableTotal = totalCount > Math.max(600, loadedCount * 1.2);

    if (loadedCount > 0) {
      return Math.min(96, 52 + (Math.log10(Math.max(loadedCount, 1)) * 18));
    }

    if (hasReliableTotal) {
      const ratio = Math.max(0, Math.min(1, loadedCount / totalCount));
      return Math.min(94, 28 + (ratio * 58));
    }

    return Math.min(84, 24 + (Math.log10(Math.max(loadedCount, 1)) * 11));
  }, [
    applications,
    isLoading,
    showInitialHydrationOverlay,
    totalLoadedCount,
  ]);

  React.useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const loaderDetail = {
      route: '/planning-updates',
      active: showInitialHydrationOverlay,
      done: !showInitialHydrationOverlay,
      progress: showInitialHydrationOverlay ? hydrationOverlayProgress : 100,
      stage: hydrationOverlayStage,
      title: hydrationOverlayTitle,
      description: hydrationOverlayDescription,
      loadedCount: totalLoadedCount,
      totalCount: hydrationOverlayTotalCount,
    };

    window[PLANNING_UPDATES_LOADER_STATE_KEY] = loaderDetail;

    window.dispatchEvent(
      new CustomEvent(PLANNING_UPDATES_LOADER_EVENT, {
        detail: loaderDetail,
      })
    );

    return undefined;
  }, [
    hydrationOverlayDescription,
    hydrationOverlayProgress,
    hydrationOverlayStage,
    hydrationOverlayTitle,
    hydrationOverlayTotalCount,
    showInitialHydrationOverlay,
    totalLoadedCount,
  ]);

  // Featured Applications Logic
  const featuredApplications = React.useMemo(() => {
    const scoreTodayApp = (app) => {
      if (app?.interesting === true) {
        return { ...app, interestScore: getBackendInterestScore(app), interestSource: 'backend' };
      }
      if (hasInterestDecision(app)) return null;

      const base = getInterestScore(app);
      const liberalScore = Math.round(base * 0.8 + (app.scale_rating ?? 0) * 3);
      if (liberalScore < 25) return null;
      return { ...app, interestScore: liberalScore, interestSource: 'fallback' };
    };

    const scoreWeeklyApp = (app) => {
      if (app?.interesting === true) {
        return { ...app, interestScore: getBackendInterestScore(app), interestSource: 'backend' };
      }
      if (hasInterestDecision(app)) return null;

      const base = getInterestScore(app);
      const added = getPlanningFeedAddedDate(app);
      const addedDate = added ? new Date(added) : referenceDate;
      const days = Math.max(0, Math.floor((referenceDate.getTime() - addedDate.getTime()) / (1000 * 60 * 60 * 24)));
      const recencyMultiplier = 1 + (0.4 / (1 + Math.exp(days - 3)));
      const scaleBonus = (app.scale_rating ?? 0) * 2;
      const composite = Math.round(base * recencyMultiplier + scaleBonus);
      if (composite < 50) return null;
      return { ...app, interestScore: composite, interestSource: 'fallback' };
    };

    // Added Today with 6am cutoff
    const todaysApps = applications.filter(app => isPlanningFeedAddedToday(app, referenceDate));

    const todayStandoutApps = todaysApps
      .map(scoreTodayApp)
      .filter(Boolean)
      .sort((a, b) => b.interestScore - a.interestScore)
      .slice(0, 12);

    // Weekly Highlights use the stable "first seen" date, not the latest rescrape.
    const isWithinLastWeek = (app) => {
      const addedDate = getPlanningFeedAddedDate(app);
      if (!addedDate) return false;
      const date = new Date(addedDate);
      if (Number.isNaN(date.getTime())) return false;
      const now = referenceDate;
      const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      return date >= weekAgo && date <= now;
    };

    const weekAll = applications.filter(app => isWithinLastWeek(app));

    const weeklyHighlights = weekAll.map(scoreWeeklyApp)
    .filter(Boolean)
    .sort((a, b) => b.interestScore - a.interestScore)
    .slice(0, 12);

    const worthWatching = applications
      .filter(app => app?.interesting === true)
      .map(app => ({ ...app, interestScore: getBackendInterestScore(app), interestSource: 'backend' }))
      .sort((a, b) => b.interestScore - a.interestScore);

    return {
      addedToday: todayStandoutApps,
      todayAll: todaysApps,
      weeklyHighlights,
      weekAll,
      worthWatching,
      trending: [...todayStandoutApps, ...weeklyHighlights].filter((app, index, arr) =>
        arr.findIndex(a => applicationIdentityKey(a || {}) === applicationIdentityKey(app || {})) === index
      )
    };
  }, [applications, referenceDate]);

  const [isCategoryLoading, setIsCategoryLoading] = useState(false);
  const [loadingProgress, _setLoadingProgress] = useState(0);
  const [hideFilters, setHideFilters] = useState(false);
  const [focusModeLoading, setFocusModeLoading] = useState(false);
  // View mode dropdown: 'default' | 'focus' | 'advanced'
  const [viewModeSetting, _setViewModeSetting] = useState(DEMO_MODE_ENABLED ? 'advanced' : 'default');
  const [walkthroughMode, setWalkthroughMode] = useState(false);
  const [walkthroughModalOpen, setWalkthroughModalOpen] = useState(false);
  const [walkthroughTopicKey, setWalkthroughTopicKey] = useState('overview');
  const [isMobileFiltersOpen, setIsMobileFiltersOpen] = useState(false);
  const [isDesktopFiltersLayout, setIsDesktopFiltersLayout] = useState(false);
  const [isTrayOverLightSurface, setIsTrayOverLightSurface] = useState(false);
  const [isDesktopTrayTriggerVisible, setIsDesktopTrayTriggerVisible] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mediaQuery = window.matchMedia('(min-width: 1024px)');
    const syncViewport = (matches) => {
      setIsDesktopFiltersLayout(matches);
      if (matches) {
        setIsMobileFiltersOpen(false);
      }
    };

    syncViewport(mediaQuery.matches);

    const handleChange = (event) => {
      syncViewport(event.matches);
    };

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }

    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, []);

  useEffect(() => {
    if (hideFilters || activeTab !== 'dashboard') {
      setIsMobileFiltersOpen(false);
    }
  }, [hideFilters, activeTab]);

  useEffect(() => {
    if (!isMobileFiltersOpen) return;

    const originalBodyOverflow = document.body.style.overflow;
    const originalHtmlOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = originalBodyOverflow;
      document.documentElement.style.overflow = originalHtmlOverflow;
    };
  }, [isMobileFiltersOpen]);

  useEffect(() => {
    if (!isMobileFiltersOpen) return;

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsMobileFiltersOpen(false);
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isMobileFiltersOpen]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const shouldHideNavbar = walkthroughModalOpen && !isDesktopFiltersLayout;
    if (!shouldHideNavbar) return;

    try {
      window.dispatchEvent(new CustomEvent('navbar-visibility', { detail: { visible: false, lock: true } }));
    } catch (_error) {
      void _error;
    }

    return () => {
      try {
        window.dispatchEvent(new CustomEvent('navbar-visibility', { detail: { visible: true, lock: false } }));
      } catch (_error) {
        void _error;
      }
    };
  }, [walkthroughModalOpen, isDesktopFiltersLayout]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (isDesktopFiltersLayout || hideFilters || activeTab !== 'dashboard' || isMobileFiltersOpen) return;

    let animationFrameId = null;
    const sampleSurface = () => {
      const trigger = mobileTrayTriggerRef.current;
      if (!trigger) return;

      const rect = trigger.getBoundingClientRect();
      if (!rect.width || !rect.height) return;

      const samplePoints = [
        { x: rect.left + rect.width * 0.2, y: rect.top + rect.height * 0.5 },
        { x: rect.left + rect.width * 0.5, y: rect.top + rect.height * 0.5 },
        { x: rect.left + rect.width * 0.8, y: rect.top + rect.height * 0.5 },
      ];

      const luminanceValues = samplePoints
        .map(point => getSurfaceLuminanceAtPoint(point.x, point.y, trigger))
        .filter(value => typeof value === 'number');

      if (luminanceValues.length === 0) {
        setIsTrayOverLightSurface(false);
        return;
      }

      const averageLuminance = luminanceValues.reduce((total, value) => total + value, 0) / luminanceValues.length;
      setIsTrayOverLightSurface(averageLuminance >= 0.7);
    };

    const scheduleSample = () => {
      if (animationFrameId != null) return;
      animationFrameId = window.requestAnimationFrame(() => {
        animationFrameId = null;
        sampleSurface();
      });
    };

    scheduleSample();

    window.addEventListener('scroll', scheduleSample, { passive: true });
    window.addEventListener('resize', scheduleSample);

    return () => {
      if (animationFrameId != null) window.cancelAnimationFrame(animationFrameId);
      window.removeEventListener('scroll', scheduleSample);
      window.removeEventListener('resize', scheduleSample);
    };
  }, [activeTab, hideFilters, isDesktopFiltersLayout, isMobileFiltersOpen]);

  useEffect(() => {
    window.setCategoryLoading = setIsCategoryLoading;
    return () => {
      delete window.setCategoryLoading;
    };
  }, []);

  const loaderRef = useRef(null);
  const mainRef = useRef(null);
  const filtersSectionRef = useRef(null);
  const latestFilingsRef = useRef(null);
  const mobileTrayTriggerRef = useRef(null);
  const walkthroughSeenTopicsRef = useRef(new Set());
  const didHandleHomepageResultsScrollRef = useRef(false);

  const scrollPlanningResultsIntoView = React.useCallback((behavior = 'smooth') => {
    if (typeof window === 'undefined') return false;

    const target = latestFilingsRef.current;
    if (!target) return false;

    const cssValue = window
      .getComputedStyle(document.documentElement)
      .getPropertyValue('--navbar-height');
    const parsed = Number.parseFloat(cssValue);
    const navOffset = (Number.isFinite(parsed) && parsed > 0 ? parsed : 96) + 18;
    const appScroller = document.getElementById('app-scroll-container');
    const canUseAppScroller =
      appScroller && appScroller.scrollHeight > appScroller.clientHeight + 4;

    if (canUseAppScroller) {
      const scrollerRect = appScroller.getBoundingClientRect();
      const targetRect = target.getBoundingClientRect();
      const targetTop = appScroller.scrollTop + targetRect.top - scrollerRect.top - navOffset;
      appScroller.scrollTo({ top: Math.max(0, targetTop), behavior });
      return true;
    }

    const targetTop = target.getBoundingClientRect().top + window.scrollY - navOffset;
    window.scrollTo({ top: Math.max(0, targetTop), behavior });
    return true;
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || !router.isReady) return undefined;

    let shouldScrollToResults = false;
    try {
      shouldScrollToResults =
        window.sessionStorage.getItem(PLANNING_UPDATES_RESULTS_SCROLL_KEY) ===
        PLANNING_UPDATES_RESULTS_SCROLL_VALUE;
    } catch (_error) {
      shouldScrollToResults = false;
    }

    if (!shouldScrollToResults) {
      didHandleHomepageResultsScrollRef.current = false;
      return undefined;
    }

    if (didHandleHomepageResultsScrollRef.current) return undefined;

    let timeoutId = null;
    let cancelled = false;
    let attempts = 0;

    const clearIntent = () => {
      try {
        window.sessionStorage.removeItem(PLANNING_UPDATES_RESULTS_SCROLL_KEY);
      } catch (_error) {
        // Non-essential cleanup.
      }
    };

    const attemptScroll = () => {
      if (cancelled) return;

      if (viewMode !== 'grid' || !scrollPlanningResultsIntoView('smooth')) {
        attempts += 1;
        if (attempts <= 80) {
          timeoutId = window.setTimeout(attemptScroll, 100);
          return;
        }
        clearIntent();
        return;
      }

      didHandleHomepageResultsScrollRef.current = true;
      clearIntent();
    };

    timeoutId = window.setTimeout(attemptScroll, 180);

    return () => {
      cancelled = true;
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [router.asPath, router.isReady, scrollPlanningResultsIntoView, viewMode]);

  useEffect(() => {
    if (typeof window === 'undefined' || !router.isReady || viewMode !== 'map') {
      return undefined;
    }

    let timeoutId = null;
    let resizeTimeoutId = null;
    let cancelled = false;
    let attempts = 0;

    const attemptScroll = () => {
      if (cancelled) return;

      if (!scrollPlanningResultsIntoView('smooth')) {
        attempts += 1;
        if (attempts <= 40) {
          timeoutId = window.setTimeout(attemptScroll, 100);
        }
        return;
      }

      resizeTimeoutId = window.setTimeout(() => {
        if (!cancelled) {
          window.dispatchEvent(new Event('resize'));
        }
      }, 360);
    };

    timeoutId = window.setTimeout(attemptScroll, 180);

    return () => {
      cancelled = true;
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
      }
      if (resizeTimeoutId !== null) {
        window.clearTimeout(resizeTimeoutId);
      }
    };
  }, [router.isReady, scrollPlanningResultsIntoView, viewMode]);

  const trackPlanningUpdatesPageAction = React.useCallback((action, context = {}) => {
    trackPlanningUpdatesEvent(action, {
      ...context,
      metadata: {
        active_tab: activeTab,
        selected_filter: selectedFilter,
        view_mode: viewMode,
        view_mode_setting: viewModeSetting,
        result_count: applications.length,
        result_count_bucket: countBucket(applications.length),
        grouped_application_bucket: countBucket(groupedApplicationCount),
        visible_group_count: visibleGroupCount,
        active_chip_bucket: countBucket(activeChips?.length || 0),
        search_mode_active: Boolean(searchModeActive),
        ...(context.metadata || {})
      }
    });
  }, [
    activeChips,
    activeTab,
    applications.length,
    groupedApplicationCount,
    searchModeActive,
    selectedFilter,
    viewMode,
    viewModeSetting,
    visibleGroupCount
  ]);

  const handleMobileFiltersOpenChange = React.useCallback((nextOpen, trigger = 'unknown') => {
    if (nextOpen !== isMobileFiltersOpen) {
      trackPlanningUpdatesPageAction(nextOpen ? 'planning_updates_mobile_filters_opened' : 'planning_updates_mobile_filters_closed', {
        placement: 'mobile_filter_tray',
        status: nextOpen ? 'opened' : 'closed',
        reason: trigger
      });
    }
    setIsMobileFiltersOpen(nextOpen);
  }, [isMobileFiltersOpen, trackPlanningUpdatesPageAction]);

  const handleSetActiveTab = React.useCallback((nextTab) => {
    if (nextTab !== activeTab) {
      trackPlanningUpdatesPageAction('planning_updates_tab_changed', {
        placement: 'page_footer',
        status: nextTab,
        reason: activeTab,
        metadata: {
          previous_tab: activeTab,
          next_tab: nextTab
        }
      });
    }
    setActiveTab(nextTab);
  }, [activeTab, trackPlanningUpdatesPageAction]);

  const handleSelectedFilterChange = React.useCallback((nextFilter, placement = 'filter_control') => {
    if (nextFilter !== selectedFilter) {
      trackPlanningUpdatesPageAction('planning_updates_feature_filter_changed', {
        placement,
        status: nextFilter,
        reason: selectedFilter,
        metadata: {
          previous_filter: selectedFilter,
          next_filter: nextFilter
        }
      });
    }
    setSelectedFilter(nextFilter);
  }, [selectedFilter, setSelectedFilter, trackPlanningUpdatesPageAction]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    if (!isDesktopFiltersLayout || hideFilters || activeTab !== 'dashboard') {
      setIsDesktopTrayTriggerVisible(false);
      return;
    }

    const sectionElement = filtersSectionRef.current;
    if (!sectionElement) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        const shouldRevealEarly = entry.boundingClientRect.top < -40 && entry.intersectionRatio < 0.65;
        const hasScrolledPastFilters = entry.boundingClientRect.bottom < 180;
        setIsDesktopTrayTriggerVisible(shouldRevealEarly || hasScrolledPastFilters);
      },
      { threshold: [0, 0.35, 0.65, 1], rootMargin: '-40px 0px 0px 0px' }
    );

    observer.observe(sectionElement);
    return () => observer.disconnect();
  }, [activeTab, hideFilters, isDesktopFiltersLayout]);

  useEffect(() => {
    const currentLoaderRef = loaderRef.current;
    if (typeof window === 'undefined' || isLoading || !hasMoreGroups) return;

    const loadMoreGroups = () => {
      if (remoteGridOwnsRendering) {
        if (hasMoreRemoteDashboardResults && !isRemoteSearchLoadingMore) {
          loadMoreRemoteSearchResults();
        }
        return;
      }
      setVisibleGroupCount((prev) => {
        const next = Math.min(prev + GRID_GROUP_BATCH_SIZE, groupedApplicationCount);
        if (next > prev) {
          trackPlanningUpdatesPageAction('planning_updates_more_groups_loaded', {
            placement: 'results_grid',
            status: countBucket(next),
            value: next - prev,
            metadata: {
              previous_visible_group_count: prev,
              next_visible_group_count: next,
              loaded_group_count: next - prev
            }
          });
        }
        return next;
      });
    };

    const maybeLoadMoreFromScrollPosition = () => {
      const viewportBottom = window.scrollY + window.innerHeight;
      const documentHeight = Math.max(
        document.documentElement?.scrollHeight || 0,
        document.body?.scrollHeight || 0
      );

      if (viewportBottom >= documentHeight - GRID_BATCH_LOAD_THRESHOLD_PX) {
        loadMoreGroups();
      }
    };

    let observer = null;
    if (currentLoaderRef && typeof IntersectionObserver !== 'undefined') {
        observer = new IntersectionObserver(
        (entries) => {
          const [entry] = entries;
          if (!entry.isIntersecting) return;

          loadMoreGroups();
          // All groups are now visible – disconnect to save resources
        },
        {
          threshold: 0.05, // More sensitive threshold
          rootMargin: "1200px" // Prefetch while the next batch is still approaching
        }
      );
      observer.observe(currentLoaderRef);
    }

    let animationFrame = null;
    const handleScroll = () => {
      if (animationFrame !== null) return;
      animationFrame = window.requestAnimationFrame(() => {
        animationFrame = null;
        maybeLoadMoreFromScrollPosition();
      });
    };

    maybeLoadMoreFromScrollPosition();
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll);

    return () => {
      if (animationFrame !== null) {
        window.cancelAnimationFrame(animationFrame);
      }
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
      if (observer && currentLoaderRef) {
        observer.unobserve(currentLoaderRef);
      }
    };
  }, [
    groupedApplicationCount,
    hasMoreGroups,
    hasMoreRemoteDashboardResults,
    isLoading,
    isRemoteSearchLoadingMore,
    loadMoreRemoteSearchResults,
    remoteGridOwnsRendering,
    searchModeActive,
    setVisibleGroupCount,
    trackPlanningUpdatesPageAction,
    visibleGroupCount,
  ]);

  const [pageReady] = useState(true);

  const currentWalkthroughTopic = WALKTHROUGH_GUIDE[walkthroughTopicKey] || WALKTHROUGH_GUIDE.overview;
  const hasAnyDashboardFilters = (
    selectedFilter !== 'all'
    || searchQuery
    || selectedCategoryFilter
    || selectedScaleCategory !== 'all'
    || hasScaleRangeFilter
    || (multipleCategoryFilters && multipleCategoryFilters.length > 0)
    || showOnlyGroupedApplications
    || Boolean(filterByAddedToday)
    || Boolean(filterByAddedYesterday)
    || Boolean(filterByScrapedDate)
    || decisionStatusFilter !== 'all'
    || ((activeChips?.length ?? 0) > 0)
  );
  const activeTextSearchLabel = React.useMemo(() => {
    const legacySearch = String(searchQuery || '').trim();
    if (legacySearch) return legacySearch;

    const workbenchSearch = String(settledSearchInput || '').trim();
    if (workbenchSearch) return workbenchSearch;

    return '';
  }, [searchQuery, settledSearchInput]);
  const textSearchSettlingSignature = React.useMemo(() => {
    if (!activeTextSearchLabel) return '';
    return `${activeRegionKey}:${activeTextSearchLabel.toLowerCase()}`;
  }, [activeRegionKey, activeTextSearchLabel]);
  const [isTextSearchSettling, setIsTextSearchSettling] = useState(false);

  useEffect(() => {
    if (!textSearchSettlingSignature) {
      setIsTextSearchSettling(false);
      return undefined;
    }

    setIsTextSearchSettling(true);
    const timeoutId = window.setTimeout(() => {
      setIsTextSearchSettling(false);
    }, SEARCH_EMPTY_STATE_GRACE_MS);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [textSearchSettlingSignature]);
  const resolvingSearchTitle = activeTextSearchLabel
    ? `Searching for "${activeTextSearchLabel}"`
    : londonScope ? 'Loading London planning applications' : 'Searching the wider planning feed';
  const resolvingSearchDescription = londonScope
    ? 'Fetching the latest available council records. Results will appear here when ready.'
    : activeTextSearchLabel
    ? (totalLoadedCount > 0
      ? `${totalLoadedCount.toLocaleString()} records indexed so far. Matching applications for "${activeTextSearchLabel}" will appear automatically as the wider feed finishes loading.`
      : `Planning Updates is still loading records for "${activeTextSearchLabel}". This is not a final no-results state.`)
    : (totalLoadedCount > 0
      ? `${totalLoadedCount.toLocaleString()} records indexed so far. Matching applications will appear automatically as the wider feed finishes loading.`
      : 'Connecting to the planning feed now. Matching applications will appear automatically as records are indexed.');
  const isGridSearchResolving = Boolean(
    isRemoteSearchLoading
    || (londonScope && !hasRemoteSearchResultSet && !remoteSearchError)
    || (activeTextSearchLabel && (isTextSearchSettling || isBackgroundHydrating))
  );

  const mobileFilterPillCount = React.useMemo(() => {
    let count = 0;

    if ((activeChips?.length ?? 0) > 0) count += 1;
    if (selectedFilter !== 'all') count += 1;
    if (selectedCategoryFilter || (multipleCategoryFilters?.length ?? 0) > 0) count += 1;
    if (selectedScaleCategory !== 'all' || hasScaleRangeFilter) count += 1;
    if (showOnlyGroupedApplications) count += 1;
    if (filterByAddedToday || filterByAddedYesterday || filterByScrapedDate) count += 1;
    if (decisionStatusFilter !== 'all') count += 1;

    return count;
  }, [
    activeChips,
    decisionStatusFilter,
    filterByAddedToday,
    filterByAddedYesterday,
    filterByScrapedDate,
    hasScaleRangeFilter,
    multipleCategoryFilters,
    selectedCategoryFilter,
    selectedFilter,
    selectedScaleCategory,
    showOnlyGroupedApplications
  ]);

  const groupedApplicationRecordCount = React.useMemo(() => {
    if (!Array.isArray(groupedApplications) || groupedApplications.length === 0) {
      return 0;
    }

    return groupedApplications.reduce((total, group) => (
      total + (Array.isArray(group?.apps) ? group.apps.length : 0)
    ), 0);
  }, [groupedApplications]);

  const latestFilingsApplicationCount = React.useMemo(() => {
    const filteredApplicationCount = Array.isArray(applications) ? applications.length : 0;

    if (showOnlyGroupedApplications) {
      return groupedApplicationRecordCount;
    }

    if (!hasAnyDashboardFilters && Number(expectedLoadedTotal) > 0) {
      return Math.max(Number(expectedLoadedTotal), filteredApplicationCount);
    }

    if (hasRemoteDashboardResultSet) {
      return Math.max(Number(resultCount) || 0, filteredApplicationCount);
    }

    return filteredApplicationCount;
  }, [
    applications,
    expectedLoadedTotal,
    groupedApplicationRecordCount,
    hasAnyDashboardFilters,
    hasRemoteDashboardResultSet,
    resultCount,
    showOnlyGroupedApplications,
  ]);

  const displayedApplicationCount = londonScope && latestFilingsApplicationCount === 0
    && (isGridSearchResolving || remoteSearchError) ? null : latestFilingsApplicationCount;

  const latestFilingsCountLabel = displayedApplicationCount == null ? 'Loading applications…' : `${latestFilingsApplicationCount.toLocaleString('en-GB')} ${
    latestFilingsApplicationCount === 1 ? 'application' : 'applications'
  }`;

  const clearAllFilters = React.useCallback(() => {
    trackPlanningUpdatesPageAction('planning_updates_filters_cleared', {
      placement: 'page_filter_controls',
      status: 'cleared',
      metadata: {
        had_dashboard_filters: Boolean(hasAnyDashboardFilters),
        active_chip_count: activeChips?.length || 0
      }
    });
    setSelectedFilter('all');
    setSearchQuery('');
    setSelectedCategoryFilter(null);
    setMultipleCategoryFilters([]);
    setCategoryFilterMode('include');
    setSelectedScaleCategory('all');
    setScaleRange({ min: 1, max: 10 });
    setFilterByScrapedDate(null);
    setDecisionStatusFilter('all');

    if (filterByAddedToday) {
      const todayDateString = new Date().toISOString().slice(0, 10);
      toggleFilterByAddedToday(todayDateString);
    }

    if (filterByAddedYesterday) {
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      const yesterdayDateString = yesterday.toISOString().slice(0, 10);
      toggleFilterByAddedYesterday(yesterdayDateString);
    }

    setShowOnlyGroupedApplications(false);
    clearSearchFilters();
    showToast('All filters cleared', 'info');
  }, [
    activeChips,
    clearSearchFilters,
    filterByAddedToday,
    filterByAddedYesterday,
    hasAnyDashboardFilters,
    setCategoryFilterMode,
    setDecisionStatusFilter,
    setFilterByScrapedDate,
    setMultipleCategoryFilters,
    setScaleRange,
    setSearchQuery,
    setSelectedCategoryFilter,
    setSelectedFilter,
    setSelectedScaleCategory,
    setShowOnlyGroupedApplications,
    showToast,
    trackPlanningUpdatesPageAction,
    toggleFilterByAddedToday,
    toggleFilterByAddedYesterday
  ]);

  const handleRegionWheelSelect = React.useCallback((option) => {
    if (!option?.href) return;
    if (option.key === activeRegionKey) return;
    const targetCouncilSlug = option.key === 'scotland'
      ? AGGREGATE_COUNCIL_SLUG
      : getCouncilConfig(option.key)?.slug || option.key;
    if (isLondonScope(targetCouncilSlug) !== londonScope) {
      clearAllFilters();
      setIsMobileFiltersOpen(false);
      setActiveTab('dashboard');
    }
    flushSync(() => {
      setOptimisticRegionKey(option.key);
    });

    trackPlanningUpdatesPageAction('planning_updates_region_wheel_selected', {
      placement: 'page_header_region_wheel',
      status: option.key,
      reason: activeRegionKey,
      metadata: {
        href: option.href,
        previous_region: activeRegionKey,
        next_region: option.key
      }
    });

    if (typeof switchCouncil === 'function') {
      React.startTransition(() => {
        switchCouncil(targetCouncilSlug);
      });
    }

    if (typeof window !== 'undefined') {
      const currentPath = `${window.location.pathname}${window.location.search || ''}`;
      if (currentPath !== option.href) {
        window.history.pushState(
          { ...(window.history.state || {}), as: option.href, url: option.href },
          '',
          option.href
        );
      }
      window.dispatchEvent(new CustomEvent(PLANNING_REGION_ROUTE_EVENT, {
        detail: { href: option.href, region: option.key, council: targetCouncilSlug },
      }));
    }
  }, [activeRegionKey, clearAllFilters, londonScope, switchCouncil, trackPlanningUpdatesPageAction]);

  const handleToggleWalkthroughMode = () => {
    const nextValue = !walkthroughMode;
    trackPlanningUpdatesPageAction('planning_updates_walkthrough_toggled', {
      placement: 'quick_actions',
      status: nextValue ? 'enabled' : 'disabled'
    });
    setWalkthroughMode(nextValue);

    if (nextValue) {
      showToast(WALKTHROUGH_GUIDE.overview.toast, 'info');
      setWalkthroughTopicKey('overview');
      setWalkthroughModalOpen(true);
      return;
    }

    setWalkthroughModalOpen(false);
    showToast('Walkthrough mode disabled.', 'info');
  };

  const handleTutorialAction = (topicKey) => {
    if (!walkthroughMode) return;

    const resolvedTopicKey = WALKTHROUGH_GUIDE[topicKey] ? topicKey : 'overview';
    const topic = WALKTHROUGH_GUIDE[resolvedTopicKey];
    trackPlanningUpdatesPageAction('planning_updates_walkthrough_topic_opened', {
      placement: 'guided_controls',
      status: resolvedTopicKey,
      metadata: {
        topic_key: resolvedTopicKey
      }
    });
    showToast(topic.toast, 'info');

    if (!walkthroughSeenTopicsRef.current.has(resolvedTopicKey)) {
      walkthroughSeenTopicsRef.current.add(resolvedTopicKey);
      setWalkthroughTopicKey(resolvedTopicKey);
      setWalkthroughModalOpen(true);
    }
  };

  // Render tab content
  const renderTabContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return renderDashboardContent();
      case 'analytics':
        return <AnalyticsTab applications={applications} />;
      case 'projects':
        return <ProjectsTab applications={applications} />;
      case 'featured':
        return <FeaturedTab applications={applications} />;
      case 'subscriptions':
        return PLANNING_ALERTS_ENABLED ? <SubscriptionsTab applications={applications} /> : renderDashboardContent();
      case 'admin':
        return <AdminTab />;
      default:
        return renderDashboardContent();
    }
  };

  const renderFilterControlsCard = ({
    compact = false,
    includeImpact = true,
    includeDisplay = true,
    dateFilterPlacement = compact ? 'mobile_filter_tray_date_filter' : 'desktop_filter_stack_date_filter'
  } = {}) => {
    if (compact) {
      if (!includeImpact && !includeDisplay) return null;

      return (
        <div className="space-y-4">
          {includeImpact && (
            <div className="pr-panel p-4">
              <div className="mb-4 flex items-end justify-between gap-3">
                <div>
                  <p className="pr-kicker">Impact scale</p>
                  <p className="pr-data mt-1">Select the scale range to include.</p>
                </div>
              </div>
              <ScaleRatingFilter compact forceShowAdvanced={viewModeSetting === 'advanced'} />
            </div>
          )}

          {includeDisplay && (
            <div className="pr-panel p-4">
              <div className="mb-4 flex items-start justify-between gap-4">
                <div>
                  <p className="pr-kicker">Status &amp; date</p>
                  <p className="pr-data mt-1">Decision outcome and filing date.</p>
                </div>
                <button
                  onClick={clearAllFilters}
                  className="pr-textlink shrink-0"
                >
                  Clear
                </button>
              </div>
              <div className="space-y-4">
                <DecisionStatusFilter />
                <ScrapedDateFilter analyticsPlacement={dateFilterPlacement} />
                {viewModeSetting === 'advanced' && (
                  <div className="flex flex-wrap gap-2">
                    <AISummariesToggle />
                    <SortByDateToggle />
                    <GroupedApplicationsToggle />
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      );
    }

    return (
      <div className="pr-panel p-5 sm:p-6">
        <div className="mb-6 flex items-center gap-4">
          <p className="pr-section-label min-w-0 flex-1">Refine results</p>
          <button
            onClick={clearAllFilters}
            className="pr-textlink shrink-0"
          >
            Clear all filters
          </button>
        </div>

        <div className={`pr-refine-grid ${includeImpact && includeDisplay ? 'pr-refine-grid--split' : ''}`}>
          {includeImpact && (
            <section className="pr-refine-section pr-refine-section--impact">
              <div className="pr-refine-section-head">
                <div>
                  <p className="pr-kicker">Impact scale</p>
                  <p className="pr-data mt-1">Range and preset</p>
                </div>
              </div>
              <ScaleRatingFilter forceShowAdvanced={viewModeSetting === 'advanced'} />
            </section>
          )}

          {includeDisplay && (
            <section className="pr-refine-section pr-refine-section--status-date">
              <div className="pr-refine-section-head">
                <div>
                  <p className="pr-kicker">Status and date</p>
                  <p className="pr-data mt-1">Decision outcome and filing date.</p>
                </div>
              </div>

              <div className="pr-status-date-grid">
                <DecisionStatusFilter />
                <ScrapedDateFilter analyticsPlacement={dateFilterPlacement} />
              </div>

              {viewModeSetting === 'advanced' && (
                <div className="mt-5 flex flex-wrap gap-2 border-t border-[var(--c-border)] pt-4">
                  <AISummariesToggle />
                  <SortByDateToggle />
                  <GroupedApplicationsToggle />
                </div>
              )}

              <div className="pr-refine-note">
                <span>Filters update results instantly.</span>
                <span>{hasAnyDashboardFilters ? 'Filters active' : 'All records included'}</span>
              </div>
            </section>
          )}
        </div>
      </div>
    );
  };

  const renderDatasetOverviewCard = ({ compact = false } = {}) => {
    if (viewModeSetting !== 'advanced') return null;

    return (
      <div className={`pr-panel ${compact ? 'p-4' : 'p-6'}`}>
        <div className="mb-4 flex items-baseline justify-between gap-4">
          <p className="pr-section-label flex-1">Dataset overview</p>
          {hasAnyDashboardFilters && (
            <span className="pr-data shrink-0 text-[var(--c-accent)]">Filters active</span>
          )}
        </div>

        <EnhancedStatsBar />
      </div>
    );
  };

  const renderMobileFiltersTray = () => {
    // London's supported search and date controls are already visible inline.
    if (retainedLondonScope) return null;
    const showTrayTrigger = activeTab === 'dashboard'
      && !hideFilters
      && !isMobileFiltersOpen
      && !selectedApplication
      && (!isDesktopFiltersLayout || isDesktopTrayTriggerVisible);
    const isLightThemeTray = !isDesktopFiltersLayout && themeMode === 'light';
    const isLightSurfaceTray = !isDesktopFiltersLayout && !isLightThemeTray && isTrayOverLightSurface;
    const triggerShellClassName = isDesktopFiltersLayout
      ? 'w-[340px] border-[var(--c-text-primary)] bg-[var(--c-surface)] shadow-[0_10px_30px_rgba(0,0,0,0.18)]'
      : `w-full max-w-md shadow-[0_10px_30px_rgba(0,0,0,0.2)] ${
          isLightSurfaceTray
            ? 'border-[#1c1a17] bg-[#1c1a17]'
            : 'border-[var(--c-text-primary)] bg-[var(--c-surface)]'
        }`;
    const triggerTitleClassName = isLightSurfaceTray && !isDesktopFiltersLayout
      ? 'text-[#f6f4ef]'
      : 'text-[var(--c-text-primary)]';
    const triggerMetaClassName = isLightSurfaceTray && !isDesktopFiltersLayout
      ? 'text-[#b3aca0]'
      : 'text-[var(--c-text-tertiary)]';
    const triggerPillClassName = isLightSurfaceTray && !isDesktopFiltersLayout
      ? 'border-[#4d483e] text-[#d8d2c6]'
      : 'border-[var(--c-border)] text-[var(--c-text-secondary)]';
    void isLightThemeTray;

    return (
      <>
      <AnimatePresence>
        {isMobileFiltersOpen && (
          <>
            <motion.button
              aria-label="Close filter tray"
              className="fixed inset-0 z-[1080] bg-black/55 backdrop-blur-[2px]"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={() => handleMobileFiltersOpenChange(false, 'backdrop')}
            />

            <motion.div
              className="fixed inset-x-0 bottom-0 z-[1090]"
              initial={{ y: '100%', opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: '100%', opacity: 0 }}
              transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            >
              <div
                className="mx-auto flex max-h-[84vh] w-full max-w-2xl flex-col overflow-hidden rounded-t-lg border border-b-0 border-[var(--c-border)] bg-[var(--c-bg)] shadow-[0_-10px_35px_rgba(0,0,0,0.25)]"
                style={{ maxHeight: '84vh' }}
              >
                <div className="sticky top-0 z-10 border-b border-[var(--c-border)] bg-[var(--c-bg)] px-4 pb-3 pt-3">
                  <div className="mx-auto mb-2 h-1 w-10 bg-[var(--c-border)]" aria-hidden />
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <h3 className="pr-heading-sm truncate">Search &amp; filters</h3>
                      <p className="pr-data mt-0.5 truncate">All controls, arranged for mobile</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleMobileFiltersOpenChange(false, 'close_button')}
                      className="border border-[var(--c-border)] bg-[var(--c-surface)] p-2 text-[var(--c-text-secondary)] transition hover:text-[var(--c-text-primary)]"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                <div
                  className="min-h-0 flex-1 space-y-4 overflow-y-auto overscroll-contain px-4 pb-[calc(env(safe-area-inset-bottom,0px)+1.25rem)] pt-4"
                  style={{ WebkitOverflowScrolling: 'touch' }}
                >
                  <SearchPanel
                    className="!rounded-2xl !px-3 !py-4"
                    tutorialMode={walkthroughMode}
                    onTutorialAction={handleTutorialAction}
                    defaultShowAdvanced={false}
                    analyticsPlacement="mobile_filter_tray_search_panel"
                    compact
                  />

                  <AppliedFiltersBar className="mt-0" />
                  {renderFilterControlsCard({
                    compact: true,
                    includeImpact: false,
                    includeDisplay: true,
                    dateFilterPlacement: 'mobile_filter_tray_date_filter'
                  })}
                  <div className="relative z-[260]">
                    <CategoryDock compact />
                  </div>
                  <ApplicationTagSelector compact />
                  {renderFilterControlsCard({
                    compact: true,
                    includeImpact: true,
                    includeDisplay: false,
                    dateFilterPlacement: 'mobile_filter_tray_date_filter'
                  })}
                  {renderDatasetOverviewCard({ compact: true })}
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {showTrayTrigger && (
        <div className="fixed inset-x-0 bottom-0 z-[1070] flex justify-center px-4 pb-[calc(env(safe-area-inset-bottom,0px)+0.85rem)] pointer-events-none">
          <motion.button
            ref={mobileTrayTriggerRef}
            type="button"
            className={`pointer-events-auto flex items-center justify-between gap-3 rounded-md border px-4 py-3 text-left transition-colors duration-200 ${triggerShellClassName}`}
            initial={{ y: 78, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ type: 'spring', damping: 22, stiffness: 300, mass: 0.85 }}
            whileTap={{ scale: 0.99 }}
            onClick={() => handleMobileFiltersOpenChange(true, 'tray_trigger')}
            aria-label="Open search and filter options"
          >
            <span className="flex min-w-0 items-center gap-3">
              <Filter className={`h-4 w-4 shrink-0 ${triggerTitleClassName}`} />
              <span className="min-w-0">
                <span className={`block truncate text-[14px] font-semibold ${triggerTitleClassName}`}>
                  Search &amp; filters
                </span>
                <span className={`block truncate font-['DM_Mono'] text-[11px] ${triggerMetaClassName}`}>
                  {mobileFilterPillCount > 0
                    ? `${mobileFilterPillCount} filter groups active`
                    : 'Refine updates'}
                </span>
              </span>
            </span>
            <span className={`inline-flex items-center border px-2.5 py-1 font-['DM_Mono'] text-[10px] font-medium uppercase tracking-[0.12em] ${triggerPillClassName}`}>
              Open
            </span>
          </motion.button>
        </div>
      )}
      </>
    );
  };

  // Dashboard content (existing functionality)
  const renderDashboardContent = () => {
    // Handle featured filters
    if (['added-today', 'weekly-highlights', 'trending', 'worth-watching'].includes(selectedFilter)) {
      return renderFeaturedContent();
    }

    return (
      <motion.div
        key="dashboard-content"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className={`space-y-6 ${!isDesktopFiltersLayout && !hideFilters ? 'pb-28' : ''}`}
      >
        {/* Search and Filters Section */}
        {!hideFilters && (
          <motion.div 
            ref={filtersSectionRef}
            className="space-y-4 relative overflow-visible"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
          >
            {retainedLondonScope ? <LondonSourceFilters onClear={clearAllFilters} /> : isDesktopFiltersLayout ? (
              <>
                <SearchPanel
                  tutorialMode={walkthroughMode}
                  onTutorialAction={handleTutorialAction}
                  defaultShowAdvanced={false}
                  analyticsPlacement="desktop_filter_stack_search_panel"
                />
                <AppliedFiltersBar />
                <details className="pr-panel p-4">
                  <summary className="cursor-pointer text-sm font-semibold">More filters: category, tags, status and date</summary>
                  <div className="mt-4 space-y-4">
                    <CategoryDock />
                    <ApplicationTagSelector />
                    {renderFilterControlsCard({ dateFilterPlacement: 'desktop_filter_stack_date_filter' })}
                    {renderDatasetOverviewCard()}
                  </div>
                </details>
              </>
            ) : (
              <>
                <div className="pr-panel px-4 py-4">
                  <div className="relative z-30 mb-4"><GlobalSearchBar variant="compact" /></div>
                  <AppliedFiltersBar />
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="pr-kicker">Search &amp; filters</p>
                      <p className="mt-1 text-sm text-[var(--c-text-secondary)]">
                        Search, categories, impact scale, and display options.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleMobileFiltersOpenChange(true, 'mobile_controls_card')}
                      className="pr-button shrink-0"
                    >
                      <SlidersHorizontal className="h-4 w-4" />
                      Open
                    </button>
                  </div>

                  <div className="mt-4 border-t border-[var(--c-border)] pt-3">
                    <p className="pr-kicker mb-2">Added recently</p>
                    <div className="pr-segment w-full">
                      <button
                        type="button"
                        aria-label="Filter by added today"
                        aria-pressed={!!filterByAddedToday}
                        className="pr-segment-item flex-1 justify-center"
                        onClick={() => {
                          const s = new Date();
                          const dateValue = `${s.getFullYear()}-${String(s.getMonth()+1).padStart(2,'0')}-${String(s.getDate()).padStart(2,'0')}`;
                          trackPlanningUpdatesPageAction('planning_updates_quick_date_filter_clicked', {
                            placement: 'mobile_quick_date_card',
                            status: 'today',
                            reason: filterByAddedToday ? 'disabled' : 'enabled'
                          });
                          toggleFilterByAddedToday(dateValue);
                        }}
                      >
                        <span>Today</span>
                        <span className="pr-segment-count">{addedTodayCount ?? '—'}</span>
                      </button>
                      <button
                        type="button"
                        aria-label="Filter by added yesterday"
                        aria-pressed={!!filterByAddedYesterday}
                        className="pr-segment-item flex-1 justify-center"
                        onClick={() => {
                          const y = new Date();
                          y.setDate(y.getDate() - 1);
                          const dateValue = `${y.getFullYear()}-${String(y.getMonth()+1).padStart(2,'0')}-${String(y.getDate()).padStart(2,'0')}`;
                          trackPlanningUpdatesPageAction('planning_updates_quick_date_filter_clicked', {
                            placement: 'mobile_quick_date_card',
                            status: 'yesterday',
                            reason: filterByAddedYesterday ? 'disabled' : 'enabled'
                          });
                          toggleFilterByAddedYesterday(dateValue);
                        }}
                      >
                        <span>Yesterday</span>
                        <span className="pr-segment-count">{addedYesterdayCount ?? '—'}</span>
                      </button>
                    </div>
                  </div>
                </div>
              </>
            )}
          </motion.div>
        )}

        {renderMobileFiltersTray()}
        
        {/* Focus Mode Indicator and Loading States */}
        {hideFilters && (
          <div className="pr-panel flex items-center justify-between gap-4 p-4">
            <div>
              <h4 className="text-sm font-semibold text-[var(--c-text-primary)]">Focus mode</h4>
              <p className="text-sm text-[var(--c-text-secondary)]">Filters hidden for a clean reading view.</p>
            </div>
            <button
              onClick={async () => {
                trackPlanningUpdatesPageAction('planning_updates_filters_shown', {
                  placement: 'focus_mode',
                  status: 'shown'
                });
                setFocusModeLoading(true);
                await new Promise(resolve => setTimeout(resolve, 100));
                setHideFilters(false);
                showToast('Filters shown', 'info');
                setTimeout(() => setFocusModeLoading(false), 150);
              }}
              disabled={focusModeLoading}
              className={`pr-button ${focusModeLoading ? 'opacity-70' : ''}`}
            >
              {focusModeLoading ? 'Showing…' : 'Show filters'}
            </button>
          </div>
        )}

        {/* Main Content Area */}
        <div
          ref={latestFilingsRef}
          id="planning-updates-results"
          data-planning-results-start="true"
          className="w-full"
        >
          <motion.div
            className="space-y-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.1 }}
          >
            {!applications.length && !isBackgroundHydrating && !isGridSearchResolving ? (
              <div className="pr-panel flex flex-col items-center justify-center px-6 py-20 text-center">
                <p className="pr-kicker mb-4">
                  {remoteSearchError ? 'Data service unavailable' : exactLocationEmptyState ? 'Exact search empty' : 'Nothing on record'}
                </p>
                <h3 className="pr-heading mb-3">
                  {remoteSearchError
                    ? 'Planning results could not be loaded'
                    : exactLocationEmptyState
                    ? 'No applications at the exact search location'
                    : 'No planning applications found'}
                </h3>
                <p className="mb-8 max-w-md text-sm leading-6 text-[var(--c-text-secondary)]">
                  {remoteSearchError
                    ? remoteSearchError
                    : exactLocationEmptyState
                    ? radiusSearchSummary?.radiusKm > 0
                      ? `We checked ${radiusSearchSummary.locationLabel || 'the selected location'} exactly and within ${radiusSearchSummary.radiusLabel}, but no applications match the current filters.`
                      : `We checked the exact search location around ${radiusSearchSummary?.locationLabel || 'the selected centre'}. Try widening the radius to 250 m or 500 m to include nearby properties.`
                    : hasAnyDashboardFilters
                    ? "No applications match your current filters. Try adjusting your search criteria or clearing some filters to see more results."
                    : "No planning data is currently available. This could be due to a temporary issue or the data is being updated. Please check back in a few moments."
                  }
                </p>

                <div className="flex flex-col gap-3 sm:flex-row">
                  {hasAnyDashboardFilters && (
                    <button onClick={clearAllFilters} className="pr-button">
                      Clear all filters
                    </button>
                  )}

                  <button
                    onClick={() => {
                      trackPlanningUpdatesPageAction('planning_updates_refresh_clicked', {
                        placement: 'empty_state',
                        status: hasAnyDashboardFilters ? 'filtered_empty' : 'empty'
                      });
                      router.reload();
                    }}
                    className="pr-button pr-button--ghost"
                  >
                    Refresh data
                  </button>

                  <button
                    onClick={() => {
                      trackPlanningUpdatesPageAction('planning_updates_map_view_clicked', {
                        placement: 'empty_state',
                        status: hasAnyDashboardFilters ? 'filtered_empty' : 'empty'
                      });
                      router.push(
                        councilConfig
                          ? `/planning-map/${councilConfig.shortCode}`
                          : '/planning-map'
                      );
                    }}
                    className="pr-button pr-button--ghost"
                  >
                    Try the map view
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                {radiusFallbackActive && (
                  <div className="pr-panel border-l-2 border-l-[var(--c-accent)] bg-[var(--c-accent-muted)] p-4 sm:p-5">
                    <p className="pr-kicker mb-2">Radius results</p>
                    <h3 className="font-['Newsreader'] text-2xl leading-tight text-[var(--c-text-primary)]">
                      No applications at the exact search location.
                    </h3>
                    <p className="mt-2 max-w-3xl text-sm leading-6 text-[var(--c-text-secondary)]">
                      Showing {radiusFallbackVisibleCount.toLocaleString()} result{radiusFallbackVisibleCount === 1 ? '' : 's'} within {radiusSearchSummary.radiusLabel} of {radiusSearchSummary.locationLabel || 'the selected centre'}. The original exact-location search returned no matching applications.
                    </p>
                  </div>
                )}
                {selectedFilter !== 'reports' && viewModeSetting === 'advanced' && <PresetFilters />}
                {selectedFilter === 'reports' ? (
                  <ReportsView applications={applications} />
                ) : (
                  <>
                {viewMode === 'grid' && (
                  <div>
                    <div className="pr-section-label mb-5">
                      <span>Latest filings</span>
                      <span className="normal-case tracking-normal text-[var(--c-text-tertiary)]">
                        {latestFilingsCountLabel}
                      </span>
                    </div>
                    <GridView
                      themeMode={themeMode}
                      groupedArray={remoteGridOwnsRendering
                        ? groupedApplications
                        : groupedApplications.slice(0, visibleGroupCount)}
                      hasMoreGroups={hasMoreGroups}
                      isLoading={isLoading}
                      applications={applications}
                      selectedImage={selectedImage}
                      setSelectedImage={handleSetSelectedImage}
                      searchQuery={searchQuery}
                      setSearchQuery={setSearchQuery}
                      selectedFilter={selectedFilter}
                      setSelectedFilter={(filter) => handleSelectedFilterChange(filter, 'grid_view')}
                      sortByDate={sortByDate}
                      expandedIndex={expandedIndex}
                      setExpandedIndex={handleCardExpand}
                      cardHoverVariants={cardHoverVariants}
                      favorites={favorites}
                      toggleFavorite={toggleFavorite}
                      showAiSummaries={showAiSummaries}
                      isCategoryLoading={isCategoryLoading}
                      loaderRef={loaderRef}
                      isResolvingSearch={isGridSearchResolving}
                      resolvingSearchTitle={resolvingSearchTitle}
                      resolvingSearchDescription={resolvingSearchDescription}
                    />
                  </div>
                )}
                {viewMode === 'timeline' && (
                  <PlanningTimeline
                    applications={applications}
                    themeMode={themeMode}
                  />
                )}
                {viewMode === 'newspaper' && newspaperView && (
                  <NewspaperView
                    themeMode={themeMode}
                    applications={applications}
                  />
                )}
                {viewMode === 'map' && mapView && (
                  <MapView />
                    )}
                  </>
                )}
              </div>
            )}
          </motion.div>
        </div>
      </motion.div>
    );
  };

  // New Featured Content Renderer
  const renderFeaturedContent = () => {
    const getFilterConfig = () => {
      switch (selectedFilter) {
        case 'added-today':
          return {
            title: 'Applications Added Today',
            subtitle: referenceDate.toLocaleDateString('en-GB', {
              weekday: 'long',
              day: 'numeric',
              month: 'long',
              timeZone: 'Europe/London',
            }),
            apps: featuredApplications.addedToday,
            allApps: featuredApplications.todayAll,
            icon: <Calendar className="w-6 h-6" />,
            emptyMessage: 'No applications added today.',
            showStandout: true
          };
          case 'weekly-highlights':
            return {
              title: 'Weekly Highlights',
              subtitle: 'Applications added in the last 7 days',
              apps: featuredApplications.weeklyHighlights,
              allApps: featuredApplications.weekAll,
              icon: <Star className="w-6 h-6" />,
              emptyMessage: 'No applications this week.',
              showStandout: true
            };
        case 'trending':
          return {
            title: 'Trending Applications',
            subtitle: 'High-interest applications across all timeframes',
            apps: featuredApplications.trending,
            icon: <TrendingUp className="w-6 h-6" />,
            emptyMessage: 'No trending applications at the moment.',
            showStandout: false
          };
        case 'worth-watching':
          return {
            title: 'Worth Watching',
            subtitle: 'Applications selected by the CityScope interest layer',
            apps: featuredApplications.worthWatching,
            icon: <Eye className="w-6 h-6" />,
            emptyMessage: 'No worth watching applications are currently selected.',
            showStandout: false
          };
        default:
          return null;
      }
    };

    const config = getFilterConfig();
    if (!config) return renderDashboardContent();

    const groupedFeaturedApps = config.apps.map(app => ({
      address: app.address,
      baseAddress: app.address,
      label: app.address,
      apps: [app],
    }));

    const groupedOtherApps = config.allApps ?
      config.allApps
        .filter(app => !config.apps.some(
          featured => applicationIdentityKey(featured || {}) === applicationIdentityKey(app || {})
        ))
        .map(app => ({
          address: app.address,
          baseAddress: app.address,
          label: app.address,
          apps: [app],
        })) : [];

    const otherAppsToShow = ['added-today', 'weekly-highlights'].includes(selectedFilter)
      ? groupedOtherApps
      : groupedOtherApps.slice(0, visibleGroupCount);

    return (
      <motion.div
        key={`featured-${selectedFilter}`}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="space-y-8"
      >
        {/* Featured Header */}
        <div className="border-t-2 border-[var(--c-text-primary)] pt-5">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h2 className="pr-heading">{config.title}</h2>
              <p className="mt-1.5 text-sm text-[var(--c-text-secondary)]">{config.subtitle}</p>
            </div>
            <button
              onClick={() => {
                trackPlanningUpdatesPageAction('planning_updates_feature_filter_reset', {
                  placement: 'featured_filter',
                  status: selectedFilter
                });
                handleSelectedFilterChange('all', 'featured_filter');
              }}
              className="pr-textlink shrink-0"
            >
              &larr; Back to all applications
            </button>
          </div>
        </div>

        {/* Content */}
        {config.apps.length === 0 ? (
          <div className="pr-panel py-24 text-center">
            <p className="pr-kicker mb-3">Nothing here yet</p>
            <p className="text-sm text-[var(--c-text-secondary)]">{config.emptyMessage}</p>
          </div>
        ) : (
          <div className="space-y-8">
            {/* Featured Applications */}
            <div>
              <p className="pr-section-label mb-6">
                {config.showStandout ? 'Worth watching' : 'Featured applications'}
                <span className="pr-data normal-case tracking-normal">{config.apps.length}</span>
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {groupedFeaturedApps.map((group, idx) => (
                  <motion.div
                    key={`featured-${idx}`}
                    initial={{ opacity: 0, scale: 0.9, y: 20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    transition={{ delay: 0.6 + idx * 0.1, duration: 0.4 }}
                  >
                    <FeaturedGroupedApplicationsCard
                      group={group}
                      showAiSummaries={showAiSummaries}
                      setSelectedImage={handleSetSelectedImage}
                      isExpanded={expandedIndex === idx}
                      onToggleExpand={() => handleCardExpand(expandedIndex === idx ? null : idx)}
                      isFavorite={favorites.some(
                        fav => applicationIdentityKey(fav || {}) === applicationIdentityKey(group.apps[0] || {})
                      )}
                      onToggleFavorite={() => toggleFavorite(group.apps[0] || {})}
                    />
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Other Applications (for Added Today and This Week) */}
            {config.showStandout && groupedOtherApps.length > 0 && (
              <div>
                <p className="pr-section-label mb-6">
                  {selectedFilter === 'added-today'
                    ? 'All other applications added today'
                    : 'All other applications this week'}
                  <span className="pr-data normal-case tracking-normal">{groupedOtherApps.length}</span>
                </p>

                  <FeaturedVirtualizedGridView
                    groupedArray={otherAppsToShow}
                    showAiSummaries={showAiSummaries}
                    selectedImage={selectedImage}
                    setSelectedImage={handleSetSelectedImage}
                    expandedIndex={expandedIndex}
                    setExpandedIndex={handleCardExpand}
                    cardHoverVariants={cardHoverVariants}
                    favorites={favorites}
                    toggleFavorite={toggleFavorite}
                  />
              </div>
            )}
          </div>
        )}
      </motion.div>
    );
  };

  const dynamicPageTitle = activeRegionKey === 'scotland'
    ? "Scotland's Planning Applications Search | City Scope"
    : `View ${planningPlaceName} Planning Applications | City Scope`;

  return (
    <>
      <Head>
        <title>{dynamicPageTitle}</title>
      </Head>
      <div className="planning-updates-root planning-register min-h-screen w-full bg-[var(--c-bg)] text-[var(--c-text-primary)]" style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
        <EnhancedLoadingScreen
          isLoading={false}
          themeMode={themeMode}
          progress={loadingProgress}
        />
        <div className="relative w-full h-full" style={{ visibility: pageReady ? 'visible' : 'hidden' }}>
        {/* Navbar is rendered in _app to avoid duplicate mounts and conflicts */}
        <div className="min-h-screen flex overflow-x-hidden">
          {!londonScope && <Sidebar />}
          {!londonScope && <HamburgerButton />}
          <main
            ref={mainRef}
            className={`flex-1 pb-4 pl-4 pr-4 transition-all duration-300 ease-in-out w-full max-w-full overflow-visible sm:pb-6 sm:pl-6 sm:pr-6 md:pb-8 md:pr-8 lg:pb-12 lg:pr-12 ${
                !londonScope && isSidebarOpen ? 'md:pl-72 lg:pl-80' : 'md:pl-8'
            }`}
            style={{ paddingTop: 'calc(var(--navbar-height, 64px) + var(--planning-region-rail-height, 0px) + clamp(1.6rem, 4.25vw, 2.65rem))' }}
          >
            <div className="max-w-7xl mx-auto overflow-visible">
              <NotificationToast />
              
              {/* Masthead */}
              <header className="mb-5 sm:mb-8">
                <div className="flex items-baseline justify-between gap-4">
                  <p className="pr-kicker pr-kicker--accent">CityScope &middot; Planning Updates</p>
                  <button
                    onClick={handleToggleWalkthroughMode}
                    className={`pr-textlink shrink-0 ${walkthroughMode ? 'text-[var(--c-accent)]' : ''}`}
                    title="Toggle walkthrough mode for guided tips"
                  >
                    {walkthroughMode ? 'Page guide: on' : 'Page guide'}
                  </button>
                </div>

                <div className="mt-4 grid gap-6 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-end">
                  <div className="min-w-0">
                    <h1 className={`pr-masthead max-w-4xl ${discoveryStyles.updatesHeadline}`}>
                      {activeRegionKey === 'scotland' ? (
                        <>Scotland&apos;s <em>planning applications</em></>
                      ) : (
                        <>View {planningPlaceName} <em>planning applications</em></>
                      )}
                    </h1>

                    <p className="mt-4 hidden max-w-2xl text-[15px] leading-7 text-[var(--c-text-secondary)] sm:block">
                      Search{' '}
                      {activeRegionKey === 'scotland' ? 'Scottish' : planningPlaceName}{' '}
                      planning applications by{' '}
                      <span className="text-[var(--c-text-primary)]">{londonScope ? 'reference number, address, proposal' : 'reference number, address, postcode, status, category,'}</span>{' '}
                      and {londonScope ? 'validation date' : 'date'}. Track development proposals, planning decisions, and local change
                      {activeRegionKey === 'scotland'
                        ? ' across Scotland.'
                        : ` across ${planningPlaceName}.`}
                    </p>

                    <CouncilCoverageNotice councilSlug={activeRegionKey} />
                    {activeRegionKey === 'london' ? <p className="mt-2 text-sm text-[var(--c-text-secondary)]">Official source records from 33 London councils. Complete register coverage is still being checked. Records without locations remain in this feed.</p> : null}
                  </div>

                  <dl className="hidden shrink-0 items-end gap-8 sm:flex lg:pb-1">
                    <div>
                      <dt className="pr-kicker">On record</dt>
                      <dd className="pr-numeral mt-1 text-[34px] leading-none sm:text-[40px]">
                        {(usesRemoteSummary ? feedSummary?.counts.total : displayedApplicationCount)?.toLocaleString('en-GB') ?? '—'}
                      </dd>
                    </div>
                    <div className="max-w-[180px]">
                      <dt className="pr-kicker">Updated</dt>
                      <dd className={`pr-data mt-2 block leading-5 ${lastUpdatedInfo.isStale ? 'text-[var(--c-error)]' : 'text-[var(--c-text-primary)]'}`}>
                        {lastUpdatedInfo.label.replace(/^Last updated\s*/i, '')}
                      </dd>
                    </div>
                  </dl>
                </div>

                <hr className="pr-double-rule mt-3 sm:mt-6" />

                <div className="mt-5 flex flex-col gap-4">
                  <PlanningUpdatesRegionSwitch
                    activeKey={activeRegionKey}
                    counts={regionCounts}
                    options={REGION_WHEEL_OPTIONS}
                    defaultCounts={REGION_COUNT_FALLBACKS}
                    onSelect={handleRegionWheelSelect}
                  />
                </div>

                {showHeaderFeedSyncNotice && (
                  <div
                    className="mt-4 max-w-2xl border-l-2 border-[var(--c-accent)] bg-[var(--c-surface)] px-4 py-3"
                    role="status"
                    aria-live="polite"
                  >
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                      <div>
                        <div className="text-sm font-semibold text-[var(--c-text-primary)]">
                          {hasUsableFeedRecords ? 'Loading the rest of the planning feed' : 'Loading the planning feed'}
                        </div>
                        <div className="mt-0.5 text-sm text-[var(--c-text-secondary)]">
                          {totalLoadedCount > 0 ? (
                            expectedLoadedTotal > 0
                              ? hasUsableFeedRecords
                                ? `${Math.min(totalLoadedCount, expectedLoadedTotal).toLocaleString()} of ${expectedLoadedTotal.toLocaleString()} records indexed. Recent applications are ready now.`
                                : `${Math.min(totalLoadedCount, expectedLoadedTotal).toLocaleString()} of ${expectedLoadedTotal.toLocaleString()} records indexed. The dashboard will appear as soon as the first results are ready.`
                              : hasUsableFeedRecords
                                ? `${totalLoadedCount.toLocaleString()} records indexed so far. Recent applications are ready now.`
                                : `${totalLoadedCount.toLocaleString()} records indexed so far. The dashboard will appear as soon as the first results are ready.`
                          ) : (
                            'Connecting to the planning feed now. The latest applications will appear automatically.'
                          )}
                        </div>
                      </div>
                      <span className="pr-data shrink-0">
                        {backgroundHydrationPercent ? `${backgroundHydrationPercent}%` : 'Updating'}
                      </span>
                    </div>
                    <div className="mt-3 h-[3px] overflow-hidden bg-[var(--c-border)]">
                      {backgroundHydrationPercent ? (
                        <motion.div
                          className="h-full bg-[var(--c-accent)]"
                          initial={false}
                          animate={{ width: `${backgroundHydrationPercent}%` }}
                          transition={{ duration: 0.35, ease: 'easeOut' }}
                        />
                      ) : (
                        <motion.div
                          className="h-full bg-[var(--c-accent)]"
                          initial={{ x: '-40%' }}
                          animate={{ x: '140%' }}
                          transition={{ duration: 1.2, repeat: Infinity, ease: 'linear' }}
                          style={{ width: '38%' }}
                        />
                      )}
                    </div>
                  </div>
                )}
              </header>
              
              {/* Comprehensive Tab Navigation */}
              {/* (Tab navigation removed) */}

              {/* Tab Content */}
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeTab}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3 }}
                >
                  {renderTabContent()}
                </motion.div>
              </AnimatePresence>
            </div>
          </main>
        </div>
        {selectedImage && <ImageModal image={selectedImage} onClose={() => handleSetSelectedImage(null)} />}
        
        {/* Planning Application Modal */}
        <PlanningApplicationModal
          isOpen={isApplicationModalOpen}
          onClose={closeApplicationModal}
          application={selectedApplication}
          showAiSummaries={showAiSummaries}
          onImageClick={handleSetSelectedImage}
          isFavorite={selectedApplication
            ? favorites.some((fav) => applicationIdentityKey(fav || {}) === applicationIdentityKey(selectedApplication || {}))
            : false}
          onToggleFavorite={selectedApplication ? () => toggleFavorite(selectedApplication) : undefined}
          initialTab={initialModalTab}
          initialDocumentUrl={initialModalDocumentUrl}
          initialImageExpanded={initialModalImageExpanded}
        />

        <AnimatePresence>
          {walkthroughModalOpen && (
            <motion.div
              key="walkthrough-modal"
              className="fixed inset-0 z-[5200] flex items-center justify-center p-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setWalkthroughModalOpen(false)}
            >
              <div className="absolute inset-0 bg-black/65 backdrop-blur-[2px]" aria-hidden />
              <motion.div
                initial={{ opacity: 0, y: 14, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 10, scale: 0.98 }}
                transition={{ duration: 0.2 }}
                onClick={(event) => event.stopPropagation()}
                className="relative w-full max-w-2xl rounded-2xl border border-[var(--c-border)] bg-[var(--c-surface)] shadow-2xl"
              >
                <div className="flex items-center justify-between border-b border-[var(--c-border)] px-5 py-4">
                  <div>
                    <p className="text-xs uppercase tracking-[0.2em] text-[var(--c-text-secondary)]">Guide</p>
                    <h3 className="text-xl font-semibold text-[var(--c-text-primary)]">{currentWalkthroughTopic.title}</h3>
                  </div>
                  <button
                    type="button"
                    onClick={() => setWalkthroughModalOpen(false)}
                    className="rounded-md border border-[var(--c-border)] px-2.5 py-1.5 text-sm text-[var(--c-text-secondary)] hover:text-[var(--c-text-primary)] hover:border-[var(--c-accent)]"
                  >
                    Close
                  </button>
                </div>

                <div className="space-y-4 px-5 py-4">
                  <p className="text-sm text-[var(--c-text-secondary)]">{currentWalkthroughTopic.description}</p>

                  <div className="flex flex-wrap gap-2">
                    {Object.entries(WALKTHROUGH_GUIDE).map(([topicKey, topic]) => (
                      <button
                        key={topicKey}
                        type="button"
                        onClick={() => {
                          trackPlanningUpdatesPageAction('planning_updates_walkthrough_topic_opened', {
                            placement: 'walkthrough_modal',
                            status: topicKey,
                            metadata: {
                              topic_key: topicKey
                            }
                          });
                          setWalkthroughTopicKey(topicKey);
                        }}
                        className={`rounded-full border px-3 py-1.5 text-xs font-medium transition ${
                          walkthroughTopicKey === topicKey
                            ? 'border-[var(--c-accent)] bg-[var(--c-accent-muted)] text-[var(--c-accent)]'
                            : 'border-[var(--c-border)] text-[var(--c-text-secondary)] hover:text-[var(--c-text-primary)] hover:border-[var(--c-accent)]/50'
                        }`}
                      >
                        {topic.title}
                      </button>
                    ))}
                  </div>

                  <div className="rounded-xl border border-[var(--c-border)] bg-[var(--c-bg)]/50 p-4">
                    <ul className="space-y-2 text-sm text-[var(--c-text-secondary)]">
                      {currentWalkthroughTopic.points.map((point) => (
                        <li key={point} className="flex items-start gap-2">
                          <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-[var(--c-accent)]" />
                          <span>{point}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="flex items-center justify-between border-t border-[var(--c-border)] px-5 py-4">
                  <span className="text-xs text-[var(--c-text-disabled)]">Tips appear as toasts when you click guided controls.</span>
                  <div className="flex items-center gap-2">
                    {walkthroughMode && (
                      <button
                        type="button"
                        onClick={handleToggleWalkthroughMode}
                        className="rounded-md border border-[var(--c-border)] px-3 py-1.5 text-sm text-[var(--c-text-secondary)] hover:border-[var(--c-accent)] hover:text-[var(--c-accent)]"
                      >
                        Disable Mode
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => setWalkthroughModalOpen(false)}
                      className="rounded-md bg-[var(--c-accent)] px-3 py-1.5 text-sm font-medium text-[var(--c-bg)] hover:brightness-110"
                    >
                      Continue
                    </button>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
          
        {/* Accessibility Helper with keyboard shortcuts */}
        <AccessibilityHelper
          onQuickFilter={(filter) => {
            trackPlanningUpdatesPageAction('planning_updates_quick_filter_selected', {
              placement: 'accessibility_helper',
              status: filter
            });
            handleSelectedFilterChange(filter, 'accessibility_helper');
          }}
          onClearFilters={clearAllFilters}
          onViewModeChange={(mode) => {
            trackPlanningUpdatesPageAction('planning_updates_view_mode_changed', {
              placement: 'accessibility_helper',
              status: mode,
              reason: viewMode
            });
            handleViewModeChange(mode);
          }}
          onSearch={() => {
            trackPlanningUpdatesPageAction('planning_updates_search_focused', {
              placement: 'accessibility_helper',
              status: 'focused'
            });
            const searchInput = document.querySelector('input[type="search"], input[placeholder*="search" i]');
            if (searchInput) searchInput.focus();
          }}
          totalApplications={latestFilingsApplicationCount}
        />
        
        <footer className="mt-20 bg-[var(--c-bg)] pb-10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 lg:px-12">
            <hr className="pr-double-rule" />
            <div className="grid grid-cols-1 gap-8 pt-8 md:grid-cols-3">
              <div>
                <p className="font-['Newsreader',Georgia,serif] text-xl font-medium text-[var(--c-text-primary)]">
                  City Scope
                </p>
                <p className="mt-2 max-w-sm text-sm leading-6 text-[var(--c-text-secondary)]">
                  A public record of planning applications, development trends, and local change across {londonScope ? 'London' : 'Scottish'} councils.
                </p>
              </div>

              {!londonScope && (
              <div>
                <p className="pr-kicker mb-3">Sections</p>
                <div className="flex flex-col items-start gap-1.5">
                  <button onClick={() => handleSetActiveTab('analytics')} className="pr-textlink block">
                    Planning analytics
                  </button>
                  <button onClick={() => handleSetActiveTab('projects')} className="pr-textlink block">
                    Development projects
                  </button>
                  <button onClick={() => handleSetActiveTab('featured')} className="pr-textlink block">
                    Featured applications
                  </button>
                  {PLANNING_ALERTS_ENABLED && (
                    <button onClick={() => handleSetActiveTab('subscriptions')} className="pr-textlink block">
                      Planning alerts
                    </button>
                  )}
                </div>
              </div>
              )}

              <div>
                <p className="pr-kicker mb-3">This page</p>
                <dl>
                  <div className="pr-fact">
                    <dt>Applications</dt>
                    <dd>{displayedApplicationCount?.toLocaleString('en-GB') ?? '—'}</dd>
                  </div>
                  <div className="pr-fact">
                    <dt>View</dt>
                    <dd className="capitalize">{viewMode}</dd>
                  </div>
                  <div className="pr-fact">
                    <dt>As of</dt>
                    <dd>{referenceDate.toLocaleDateString('en-GB', { timeZone: 'Europe/London' })}</dd>
                  </div>
                </dl>
              </div>
            </div>
          </div>
        </footer>
        </div>
      </div>
    </>
  );
};

export default PlanningUpdatesPage;
